import { CornerTable } from "../../v2/components/CornerTable.jsx";
import { withListOverflow } from "../../v2/components/ListOverflow.jsx";
export const UploadStep = withListOverflow(UploadPreviewListOwner);
// src/features/admin/ImportWizard.jsx
//
// INGEST-FRAMEWORK-S1: 3-step import wizard (Upload → Map → Validate).
// Replaces the placeholder in Admin → Data → Import.
//
// Permission gate: viewer role sees a read-only notice. editor/admin can proceed.

import React, { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { UploadCloud } from "lucide-react";
import { T } from "../../app/tokens.js";
import { APP_NAME } from "../../constants/branding.js";
import {
  stageImportFile,
  getSourceProfiles,
  saveSourceProfile,
  updateSourceProfile,
  deleteSourceProfile,
  validateStagedSession,
  getStagedRows,
  assignSessionLocation,
  confirmDataType,
  acknowledgeAnomaly,
  getBadgingReview,
} from "../../data/importPipelineClient.js";
import { getLocations } from "../../data/locationsClient.js";
import { getImportSession, confirmAsOfDate } from "../../data/importSessionsClient.js";
import { ImportReviewPanel } from "../imports/components/ImportReviewPanel.jsx";

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function buildLocationLabel({ id, market, locationAlias }) {
  let label = `${id} — ${market || ""}`;
  if (locationAlias) label += ` · ${locationAlias}`;
  if (label.length > 48) label = `${label.slice(0, 47)}\u2026`;
  return label;
}

// ─── Style constants ──────────────────────────────────────────────────────────

const S = {
  wizard: {
    background: T.card,
    border: `1px solid ${T.border}`,
    borderRadius: 10,
    overflow: "hidden",
  },
  stepper: {
    display: "flex",
    alignItems: "center",
    padding: "16px 24px",
    borderBottom: `1px solid ${T.border}`,
    background: T.pageBg,
    gap: 0,
  },
  stepperItem: (active, done) => ({
    display: "flex",
    alignItems: "center",
    gap: 8,
    flex: 1,
    opacity: active || done ? 1 : 0.4,
  }),
  stepCircle: (active, done) => ({
    width: 26,
    height: 26,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: T.fsXs,
    fontWeight: T.fwBold,
    flexShrink: 0,
    background: done ? T.statusDoneBg : active ? T.blueDeep : T.lightGray,
    color: done ? T.statusDoneText : active ? T.white : T.mid,
    border: done
      ? `1.5px solid ${T.statusDoneBorder}`
      : active
      ? `1.5px solid ${T.blueDeep}`
      : `1.5px solid ${T.border}`,
    transition: "all 0.15s",
  }),
  stepLabel: (active, done) => ({
    fontSize: T.fsSm5,
    fontWeight: active ? T.fwSemibold : T.fwNormal,
    color: active ? T.dark : done ? T.statusDoneText : T.mid,
    whiteSpace: "nowrap",
  }),
  stepConnector: {
    flex: 1,
    height: 1,
    background: T.border,
    margin: "0 8px",
  },
  body: {
    padding: "24px 28px",
    minHeight: 260,
  },
  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 28px",
    borderTop: `1px solid ${T.border}`,
    background: T.pageBg,
  },
  btnBase: {
    padding: "7px 18px",
    borderRadius: 6,
    fontSize: T.fsSm5,
    fontWeight: T.fwSemibold,
    cursor: "pointer",
    border: "none",
    transition: "opacity 0.1s",
  },
  btnPrimary: {
    background: T.blueDeep,
    color: T.white,
  },
  btnSecondary: {
    background: "transparent",
    color: T.mid,
    border: `1px solid ${T.border}`,
  },
  btnDanger: {
    background: "transparent",
    color: T.red,
    border: `1px solid ${T.red}`,
  },
  btnLinkPlain: {
    background: "transparent",
    color: T.mid,
    border: "none",
    padding: "7px 0",
  },
};

// ─── XLSX confirm-sheet: key mapped columns to tint in the column preview ─────
const XLSX_KEY_MAPPED_COLUMNS = ["Worker Type", "Contractor Type", "Work Location"];

// ─── INGEST-XLSX-DEFER-TO-CSV-S1 ───────────────────────────────────────────────
// Native XLSX detection/parsing (SheetJS sheet+header-row picker below) has
// failed detection multiple times in the field. Rather than delete the work,
// it's gated behind this flag so the flow is dormant but easy to revisit —
// flip back to `true` to re-enable prepareXlsxFile/changeXlsxSheet/
// changeXlsxHeaderRow/confirmXlsxSheet and their UI. While disabled, dropped
// .xlsx files show a friendly redirect to CSV instead.
const XLSX_NATIVE_IMPORT_ENABLED = false;
const XLSX_REDIRECT_MESSAGE =
  "XLSX isn't supported yet - please convert to CSV (Save As → CSV) and re-upload.";

// ─── Confidence badge ─────────────────────────────────────────────────────────

function ConfidenceBadge({ confidence }) {
  const map = {
    high:   { bg: T.statusDoneBg,  color: T.statusDoneText,  label: "High" },
    medium: { bg: T.warnAmberBg,   color: T.warnAmberText,   label: "Medium" },
    low:    { bg: T.utilRed,        color: T.red,             label: "Low" },
  };
  const { bg, color, label } = map[confidence] || map.low;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "1px 7px",
        borderRadius: 10,
        fontSize: T.fsXs,
        fontWeight: T.fwSemibold,
        background: bg,
        color,
      }}
    >
      {label}
    </span>
  );
}

// ─── Canonical field list (for editable mapping select) ───────────────────────

const CANONICAL_FIELDS = [
  "leaseIdSpacebase",
  "lessee",
  "vendor",
  "areas",
  "areaUnit",
  "type",
  "opexStructure",
  "startDate",
  "endDate",
  "leaseStatus",
  "annualRent",
  "annualRentPerSqft",
  "opex",
  "capex",
  "size",
  "currency",
  "breakDate",
  "breakNoticeMonths",
  "renewalNoticeMonths",
  "renewalCount",
  "renewalTermYears",
  "escalationType",
  "escalationRate",
  "holdoverPercent",
  "freeRentMonths",
  "tiaTotalAmount",
  "securityDepositAmount",
  "securityDepositType",
  "camDescription",
  "notes",
  "possessionDate",
  "lessorContact",
  "rentPaymentType",
  "termType",
  "_yc_2024_rent",
  "_yc_2024_opex",
  "_yc_2024_total_cost",
  "_yc_2025_rent",
  "_yc_2025_opex",
  "_yc_2025_total_cost",
  "_yc_2026_rent",
  "_yc_2026_opex",
  "_yc_2026_total_cost",
  "_yc_2027_rent",
  "_yc_2027_opex",
  "_yc_2027_total_cost",
  "_yc_2028_rent",
  "_yc_2028_opex",
  "_yc_2028_total_cost",
  "_yc_2029_rent",
  "_yc_2029_opex",
  "_yc_2029_total_cost",
  "_yc_2030_rent",
  "_yc_2030_opex",
  "_yc_2030_total_cost",
  "_yc_2031_rent",
  "_yc_2031_opex",
  "_yc_2031_total_cost",
  "_yc_2032_rent",
  "_yc_2032_opex",
  "_yc_2032_total_cost",
  "_yc_2033_rent",
  "_yc_2033_opex",
  "_yc_2033_total_cost",
  "_yc_2034_rent",
  "_yc_2034_opex",
  "_yc_2034_total_cost",
  "_yc_2035_rent",
  "_yc_2035_opex",
  "_yc_2035_total_cost",
];

// INGEST-PIPELINE-TYPE-AWARE-S1: Badging/Utilization canonical fields — a
// separate, much smaller field list from the Leases CANONICAL_FIELDS above.
// Kept apart so mapping a leases file never surfaces badging fields (and
// vice versa) in the "map to…" / "change" dropdowns.
const BADGING_CANONICAL_FIELDS = [
  "location_id",
  "month",
  "avg_daily_unique_badges",
  "peak_daily_unique_badges",
  "source_system",
  "office_id",
  "office",
  "market",
  "date",
  "source",
  "present_total",
  "present_employees",
  "present_visitors",
  "worker_class",
  "week_start",
  "eligible",
  "compliant_10plus",
  "compliance_pct",
  "monthly_avg_present",
  "monthly_peak",
  "note",
  "present",
  "attendance_pct",
];

const BADGING_LABELS = {
  location_id:              "Location ID (match key)",
  month:                    "Month (match key)",
  avg_daily_unique_badges:  "Avg daily unique badges",
  peak_daily_unique_badges: "Peak daily unique badges",
  source_system:            "Source system",
  office_id:               "Office ID (match key)",
  office:                  "Office",
  market:                  "Market",
  date:                    "Date (match key)",
  source:                  "Daily source (match key)",
  present_total:           "Present total",
  present_employees:       "Present employees",
  present_visitors:        "Present visitors",
  worker_class:            "Worker class (match key)",
  week_start:              "Week start (match key)",
  eligible:                "Eligible count",
  compliant_10plus:        "Compliant 10+ days",
  compliance_pct:          "Compliance percentage",
  monthly_avg_present:     "Monthly average present",
  monthly_peak:            "Monthly peak",
  note:                    "Compliance note",
  present:                 "Present count",
  attendance_pct:          "Attendance percentage",
};

// INGEST-HEADCOUNT-TYPE-S1: Headcount canonical fields — the adapter already
// aggregates raw rows to per-office { location_id, ft, vc, ic }, so this is a
// small, separate list from both Leases and Badging.
// HEADCOUNT-IMPORT-S1-S4: Added pivot_worker_type and pivot_contractor_type so
// operators can map the Worker Type / Contractor Type columns to pivot targets
// that tell the importer to count-by-type rather than map to a scalar.
const HEADCOUNT_CANONICAL_FIELDS = [
  "location_id",
  "source_location_name",
  "ft",
  "vc",
  "ic",
  "pivot_worker_type",
  "pivot_contractor_type",
];

const HEADCOUNT_LABELS = {
  location_id:           "Location ID (match key)",
  source_location_name:  "Work Location (crosswalk name)",
  ft:                    "Full-time count",
  vc:                    "Vendor contractor count",
  ic:                    "Independent contractor count",
  pivot_worker_type:     "Worker type (count by type)",
  pivot_contractor_type: "Contractor type (vendor/independent split)",
};

function canonicalFieldsForDataType(dataTypeKey) {
  if (dataTypeKey === "badging") return BADGING_CANONICAL_FIELDS;
  if (dataTypeKey === "headcount") return HEADCOUNT_CANONICAL_FIELDS;
  return CANONICAL_FIELDS;
}

// INGEST-PIPELINE-TYPE-AWARE-S1: human-readable type labels for the Profiles
// list — kept in sync with the registered keys in server typeRegistry.js
// (leases, badging, headcount). Falls back to the raw key for any future
// type not yet listed here.
const DATA_TYPE_LABELS = {
  leases:    "Leases",
  badging:   "Badging / Utilization",
  headcount: "Headcount",
};

function canonicalLabel(f) {
  if (BADGING_LABELS[f]) return BADGING_LABELS[f];
  if (HEADCOUNT_LABELS[f]) return HEADCOUNT_LABELS[f];
  const ycMatch = f.match(/^_yc_(\d{4})_(rent|opex|total_cost)$/);
  if (ycMatch) {
    const year = ycMatch[1];
    const sub = { rent: "Rent", opex: "OpEx", total_cost: "Total cost" }[ycMatch[2]];
    return `Year ${year} — ${sub}`;
  }
  return f.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase());
}

// ─── Wizard status strip ──────────────────────────────────────────────────────
//
// Persistent one-liner below the Stepper showing step · phase · key counts.
// This is the primary "am I on track?" signal for the user.

function buildStatusText({ step, stageResult, validationStatus, needsEyesCount }) {
  const phaseLabels = ["Upload", "Mapping", "Validate", "Review", "Done"];
  const phase = phaseLabels[step - 1] ?? "—";
  const prefix = `Step ${step} of 5 · ${phase}`;

  if (step === 1) {
    const detail = stageResult
      ? "File already staged - continue or swap it out"
      : "Drag a CSV or PDF here to begin";
    return `${prefix} · ${detail}`;
  }

  if (step === 2) {
    const profilePart = stageResult?.sourceProfile
      ? `Profile recognized`
      : stageResult?.suggestedProfileName
      ? `Profile detected`
      : "No profile - manual mapping";
    const confirmPart =
      needsEyesCount > 0
        ? `${needsEyesCount} to confirm`
        : "All confirmed";
    const readyPart = needsEyesCount === 0 ? " · Ready to validate" : "";
    return `${prefix} · ${profilePart} · ${confirmPart}${readyPart}`;
  }

  if (step === 3) {
    const detail =
      !validationStatus
        ? "Running checks…"
        : validationStatus === "passed"
        ? "Passed - ready to review diffs"
        : validationStatus === "warnings"
        ? "Warnings to note - keep as-is or fix, then continue"
        : "Errors found - fix before continuing";
    return `${prefix} · ${detail}`;
  }

  if (step === 4) {
    return `${prefix} · Review changes and apply when ready`;
  }

  return `${prefix} · Import complete`;
}

function WizardStatusStrip({ step, stageResult, validationStatus, needsEyesCount }) {
  const text = buildStatusText({ step, stageResult, validationStatus, needsEyesCount });
  return (
    <div
      data-testid="wizard-status-strip"
      style={{
        padding: "7px 24px",
        background: T.pageBg,
        borderBottom: `1px solid ${T.border}`,
        fontSize: T.fsSm5,
        color: T.mid,
        display: "flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      <span style={{ fontWeight: T.fwSemibold, color: T.dark }}>{text.split(" · ")[0]}</span>
      {text.split(" · ").slice(1).map((part, i) => (
        <React.Fragment key={i}>
          <span style={{ color: T.light, fontSize: T.fsXs }}>·</span>
          <span>{part}</span>
        </React.Fragment>
      ))}
    </div>
  );
}

// ─── Stepper ──────────────────────────────────────────────────────────────────

const STEPS = ["Upload", "Map", "Validate", "Review", "Done"];

function Stepper({ step }) {
  return (
    <div style={S.stepper}>
      {STEPS.map((label, i) => {
        const num    = i + 1;
        const active = step === num;
        const done   = step > num;
        return (
          <React.Fragment key={label}>
            <div style={S.stepperItem(active, done)}>
              <div style={S.stepCircle(active, done)}>
                {done ? "✓" : num}
              </div>
              <span style={S.stepLabel(active, done)}>{label}</span>
            </div>
            {i < STEPS.length - 1 && <div style={S.stepConnector} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Step 1: Upload ───────────────────────────────────────────────────────────
//
// PDF-ABSTRACTION-MODEL-OVERHAUL: multi-file PDF support.
// - Dropping/selecting PDF files adds them to a staged list instead of
//   immediately uploading. The user can add more files or remove any before
//   clicking "Abstract [N] documents →".
// - For CSV/XLSX files, the existing auto-upload-on-drop behaviour is preserved.
// - Zero extracted fields is surfaced as an extraction error with Retry and
//   "Upload different file(s)" affordances.

// ── Smart filename → location suggestion ──────────────────────────────────────
// Given a list of PDF filenames and the full locations list, returns the best
// matching location (by ID literal match first, then alias token overlap).
function suggestLocationFromFilenames(filenames, locations) {
  if (!filenames?.length || !locations?.length) return null;
  const haystack = filenames.map((f) => f.toLowerCase()).join(" ");

  // Pass 1: ID literal present in any filename (highest confidence)
  for (const loc of locations) {
    if (haystack.includes(loc.id.toLowerCase())) return loc;
  }

  // Pass 2: alias token overlap — score by fraction of alias words matched
  let best = null;
  let bestScore = 0;
  for (const loc of locations) {
    const alias = (loc.location_alias || "").toLowerCase();
    if (!alias) continue;
    const words = alias.split(/[\s\-_,]+/).filter((w) => w.length > 2);
    if (!words.length) continue;
    const matched = words.filter((w) => haystack.includes(w)).length;
    const score   = matched / words.length;
    if (score > bestScore) {
      bestScore = score;
      best      = loc;
    }
  }
  // Require at least half the alias words to match before pre-selecting
  return bestScore >= 0.5 ? best : null;
}

const UPLOAD_SPIN_STYLE = `
@keyframes upload-ring-spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}
`;

let _uploadSpinStyleInjected = false;
function ensureUploadSpinStyle() {
  if (_uploadSpinStyleInjected) return;
  _uploadSpinStyleInjected = true;
  const el = document.createElement("style");
  el.textContent = UPLOAD_SPIN_STYLE;
  document.head.appendChild(el);
}

function UploadPreviewListOwner({ onComplete, busy, setBusy, existingResult, onClearExisting }) {
  const [dragOver, setDragOver]           = useState(false);
  // pdfFiles: list of PDF File objects staged for multi-doc upload (not yet sent).
  const [pdfFiles, setPdfFiles]           = useState([]);
  const [csvFile, setCsvFile]             = useState(null);
  const [error, setError]                 = useState(null);
  const [staged, setStaged]               = useState(null); // post-upload confirmation state
  const [extractionError, setExtractionError] = useState(null); // PDF-specific extraction failure
  // Location picker (PDF sessions only)
  const [locationOptions, setLocationOptions]   = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [locationSuggested, setLocationSuggested]   = useState(false);
  const [locationAssigning, setLocationAssigning]   = useState(false);
  const [uploadPhase, setUploadPhase]     = useState(null); // null | "uploading" | "abstracting"
  // INGEST-PIPELINE-TYPE-AWARE-S1: Step 1 detect-and-confirm data type state.
  const [dataTypeOverride, setDataTypeOverride] = useState(null); // explicit user selection, else use detected
  const [retyping, setRetyping] = useState(false); // re-staging with a new overrideDataType
  const [retypeFailed, setRetypeFailed] = useState(false); // last re-stage attempt errored; block Continue
  // INGEST-XLSX-SHEETJS-S1: sheet/header-row picker state for dropped .xlsx files.
  const [xlsxStage, setXlsxStage] = useState(null); // { file, XLSX, workbook, sheetNames, sheetName, headerRowIndex, grid }
  const [xlsxLoading, setXlsxLoading] = useState(false);
  const [xlsxError, setXlsxError] = useState(null);
  const fileRef = useRef();

  useEffect(() => { ensureUploadSpinStyle(); }, []);

  // ── Trigger upload for CSV/XLSX (non-PDF single file) ─────────────────────
  const uploadCsvFile = useCallback(async (f) => {
    setCsvFile(f);
    setError(null);
    setExtractionError(null);
    // INGEST-PIPELINE-TYPE-AWARE-S1: a fresh upload must never inherit a data
    // type override picked for a previously-staged file (e.g. after "Upload
    // a different file"); always start ambiguous files at blank selection.
    setDataTypeOverride(null);
    setRetyping(false);
    setBusy(true);
    setUploadPhase("uploading");
    try {
      const result = await stageImportFile(f);
      setStaged({ result, stagedFiles: [f] });
    } catch (err) {
      setError(err.message || "Upload failed");
      setCsvFile(null);
    } finally {
      setBusy(false);
      setUploadPhase(null);
    }
  }, [setBusy]);

  // ── Trigger upload for the current PDF files list ─────────────────────────
  const uploadPdfFiles = useCallback(async () => {
    if (pdfFiles.length === 0) return;
    setError(null);
    setExtractionError(null);
    setBusy(true);
    setUploadPhase("abstracting");
    try {
      const result = await stageImportFile(pdfFiles);
      // Block advancement if the LLM extracted 0 useful fields.
      // extractedFieldCount on a successful response is the total count of
      // canonical fields; non-null (grounded) fields are what matters here.
      const groundedCount = (result.extractedFields ?? []).filter(
        (f) => f.value !== null && f.value !== undefined,
      ).length;
      if (groundedCount === 0) {
        setExtractionError({
          message:     "The AI could not extract any grounded lease fields from these documents.",
          reason:      "extraction_empty",
          parseErrors: result.parseErrors ?? [],
        });
        return;
      }
      setStaged({ result, stagedFiles: pdfFiles });
    } catch (err) {
      if (err.code === "pdf_extraction_failed") {
        setExtractionError({
          message:     err.message,
          reason:      err.reason,
          parseErrors: err.parseErrors ?? [],
        });
      } else {
        setError(err.message || "Upload failed");
      }
    } finally {
      setBusy(false);
      setUploadPhase(null);
    }
  }, [pdfFiles, setBusy]);

  // ── Fetch locations for the PDF location picker ───────────────────────────
  // Runs once when a PDF staging result arrives. Also computes the smart
  // suggestion from the staged filenames and pre-selects if confident.
  useEffect(() => {
    if (!staged) return;
    const { result, stagedFiles } = staged;
    if (result.detectedSourceType !== "pdf_abstraction") return;

    let cancelled = false;
    getLocations()
      .then((locs) => {
        if (cancelled) return;
        setLocationOptions(locs);
        const suggestion = suggestLocationFromFilenames(
          (stagedFiles ?? []).map((f) => f.name),
          locs,
        );
        if (suggestion) {
          setSelectedLocationId(suggestion.id);
          setLocationSuggested(true);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [staged]);

  // ── INGEST-XLSX-SHEETJS-S1: lazy-parse a dropped .xlsx file and show the ──
  // sheet/header-row picker instead of uploading immediately.
  const prepareXlsxFile = useCallback(async (f) => {
    setError(null);
    setExtractionError(null);
    setXlsxError(null);
    setXlsxStage(null);
    setXlsxLoading(true);
    try {
      const { parseWorkbookFile, getSheetGrid } = await import("../../utils/xlsxParser.js");
      const { workbook, sheetNames } = await parseWorkbookFile(f);
      if (sheetNames.length === 0) {
        setXlsxError("This workbook has no sheets with data to import.");
        return;
      }
      const sheetName = sheetNames[0];
      const { grid, headerRowIndex } = getSheetGrid(workbook, sheetName);
      setXlsxStage({ file: f, workbook, sheetNames, sheetName, headerRowIndex, grid });
    } catch (err) {
      setXlsxError(err.message || "Could not read this Excel file.");
    } finally {
      setXlsxLoading(false);
    }
  }, []);

  // Re-derive the preview grid + auto-detected header row when the user
  // switches sheets in the picker.
  const changeXlsxSheet = useCallback(async (sheetName) => {
    const { getSheetGrid } = await import("../../utils/xlsxParser.js");
    setXlsxStage((prev) => {
      if (!prev) return prev;
      const { grid, headerRowIndex } = getSheetGrid(prev.workbook, sheetName);
      return { ...prev, sheetName, grid, headerRowIndex };
    });
  }, []);

  const changeXlsxHeaderRow = useCallback((headerRowIndex) => {
    setXlsxStage((prev) => (prev ? { ...prev, headerRowIndex } : prev));
  }, []);

  const cancelXlsxStage = useCallback(() => {
    setXlsxStage(null);
    setXlsxError(null);
  }, []);

  // Convert the confirmed sheet/header row into a CSV File and hand it to
  // the existing CSV upload path — no downstream pipeline changes needed.
  const confirmXlsxSheet = useCallback(async () => {
    if (!xlsxStage) return;
    const { workbook, sheetName, headerRowIndex, file } = xlsxStage;
    try {
      const { buildCsvFileFromSheet } = await import("../../utils/xlsxParser.js");
      const csvFile = buildCsvFileFromSheet(workbook, sheetName, headerRowIndex, file.name);
      setXlsxStage(null);
      uploadCsvFile(csvFile);
    } catch (err) {
      setXlsxError(err.message || "Could not convert this sheet to import it.");
    }
  }, [xlsxStage, uploadCsvFile]);

  // ── Handle dropped / selected files ──────────────────────────────────────
  const addFiles = useCallback((incoming) => {
    const arr = Array.from(incoming);
    const invalid = arr.find((f) => !f.name.match(/\.(csv|txt|xlsx|pdf)$/i));
    if (invalid) {
      setError("Only CSV and PDF files are accepted.");
      return;
    }
    const oversized = arr.find((f) => f.size > 10 * 1024 * 1024);
    if (oversized) {
      setError("File too large - maximum 10 MB per file.");
      return;
    }
    setError(null);
    setExtractionError(null);

    const allPdfs = arr.every((f) => /\.pdf$/i.test(f.name));
    const hasCsv  = arr.some((f) => !(/\.pdf$/i.test(f.name)));

    if (hasCsv && arr.length > 1) {
      setError("Please upload CSV files one at a time.");
      return;
    }
    if (hasCsv) {
      const f = arr[0];
      if (/\.xlsx$/i.test(f.name)) {
        if (XLSX_NATIVE_IMPORT_ENABLED) {
          // INGEST-XLSX-SHEETJS-S1: XLSX needs a sheet/header-row confirmation
          // step before it can be converted and staged.
          prepareXlsxFile(f);
          return;
        }
        // INGEST-XLSX-DEFER-TO-CSV-S1: native XLSX support is dormant — show
        // a friendly redirect instead of running the (unreliable) header-detect
        // flow. No prepareXlsxFile call, no picker/preview UI.
        setError(XLSX_REDIRECT_MESSAGE);
        return;
      }
      // Non-PDF, non-XLSX: auto-upload immediately (existing CSV behaviour).
      uploadCsvFile(f);
      return;
    }
    if (allPdfs) {
      // PDF: add to staged list for multi-doc batch; don't upload yet.
      setPdfFiles((prev) => {
        // Deduplicate by name.
        const names = new Set(prev.map((f) => f.name));
        const fresh  = arr.filter((f) => !names.has(f.name));
        return [...prev, ...fresh];
      });
    }
  }, [uploadCsvFile, prepareXlsxFile]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) addFiles(e.dataTransfer.files);
  }, [addFiles]);

  const onInputChange = useCallback((e) => {
    if (e.target.files.length > 0) addFiles(e.target.files);
    // Reset the input so re-selecting the same files works.
    e.target.value = "";
  }, [addFiles]);

  const removePdfFile = useCallback((name) => {
    setPdfFiles((prev) => prev.filter((f) => f.name !== name));
  }, []);

  const resetAll = useCallback(() => {
    setPdfFiles([]);
    setCsvFile(null);
    setExtractionError(null);
    setError(null);
    setStaged(null);
    // INGEST-PIPELINE-TYPE-AWARE-S1: clear any prior explicit type choice so a
    // fresh (or newly ambiguous) file always starts at the blank "Select a
    // data type" state, never silently inheriting the previous file's pick.
    setDataTypeOverride(null);
    setRetyping(false);
  }, []);

  const dropStyle = {
    border: `2px dashed ${dragOver ? T.blueDeep : (pdfFiles.length > 0 ? T.statusDoneBorder : T.border)}`,
    borderRadius: 8,
    padding: pdfFiles.length > 0 ? "18px 24px" : "36px 24px",
    textAlign: "center",
    background: dragOver ? T.blueSoft : T.pageBg,
    transition: "all 0.15s",
    cursor: "pointer",
  };

  // ── PDF extraction failure ────────────────────────────────────────────────
  if (extractionError) {
    const reasonLabels = {
      llm_unavailable:   "The AI extraction service is currently unreachable.",
      insufficient_text: "The PDF does not contain enough readable text. Is it a scanned image?",
      extraction_empty:  "The AI could not find any grounded lease fields in these documents.",
    };
    const reasonDetail = reasonLabels[extractionError.reason] ?? null;

    return (
      <div>
        <div
          role="alert"
          data-testid="pdf-extraction-error"
          style={{
            padding: "14px 16px",
            borderRadius: 7,
            background: T.alertErrBg,
            border: `1px solid ${T.redRing}`,
            marginBottom: 16,
          }}
        >
          <p style={{ margin: "0 0 6px 0", fontWeight: T.fwSemibold, color: T.red, fontSize: T.fsSm5 }}>
            PDF extraction failed
          </p>
          <p style={{ margin: "0 0 4px 0", fontSize: T.fsSm5, color: T.dark }}>
            {extractionError.message}
          </p>
          {reasonDetail && extractionError.reason !== "extraction_empty" && (
            <p style={{ margin: "0 0 4px 0", fontSize: T.fsXs, color: T.mid }}>
              {reasonDetail}
            </p>
          )}
          {extractionError.reason === "llm_unavailable" && (
            <p style={{ margin: "4px 0 0 0", fontSize: T.fsXs, color: T.mid }}>
              Try again in a few minutes, or contact your administrator to check the AI service status.
            </p>
          )}
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button
            type="button"
            style={{ ...S.btnBase, ...S.btnPrimary }}
            data-testid="extraction-retry-btn"
            onClick={() => { setExtractionError(null); setError(null); uploadPdfFiles(); }}
            disabled={pdfFiles.length === 0 || busy}
          >
            Retry
          </button>
          <button
            type="button"
            style={{ ...S.btnBase, ...S.btnSecondary }}
            data-testid="extraction-upload-different-btn"
            onClick={resetAll}
          >
            Upload different file(s)
          </button>
        </div>
      </div>
    );
  }

  // ── INGEST-XLSX-SHEETJS-S1: XLSX read error ───────────────────────────────
  if (xlsxError) {
    return (
      <div>
        <div
          role="alert"
          data-testid="xlsx-error"
          style={{
            padding: "14px 16px",
            borderRadius: 7,
            background: T.alertErrBg,
            border: `1px solid ${T.redRing}`,
            marginBottom: 16,
            fontSize: T.fsSm5,
            color: T.dark,
          }}
        >
          {xlsxError}
        </div>
        <button
          type="button"
          style={{ ...S.btnBase, ...S.btnSecondary }}
          data-testid="xlsx-upload-different-btn"
          onClick={cancelXlsxStage}
        >
          Upload a different file
        </button>
      </div>
    );
  }

  // ── INGEST-XLSX-SHEETJS-S1: lazily loading SheetJS + parsing the workbook ─
  if (xlsxLoading) {
    return (
      <div data-testid="xlsx-loading" style={{ padding: "24px 0", textAlign: "center", color: T.mid, fontSize: T.fsSm5 }}>
        Reading workbook…
      </div>
    );
  }

  // ── INGEST-XLSX-SHEETJS-S1 / INGEST-XLSX-UPLOAD-FIXES-S1: sheet / header-row picker + preview ──
  if (xlsxStage) {
    const { sheetNames, sheetName, headerRowIndex, grid, file } = xlsxStage;
    const headerRow    = grid[headerRowIndex] ?? [];
    const rowOptions   = grid.slice(0, Math.min(grid.length, 15));
    const columnNames  = headerRow
      .map((h) => (h === null || h === undefined || String(h).trim() === "" ? null : String(h).trim()))
      .filter(Boolean);
    const dataRowCount = Math.max(0, grid.length - headerRowIndex - 1);

    return (
      <div>
        <p style={{ fontSize: T.fsBase, color: T.dark, marginBottom: 4, fontWeight: T.fwSemibold }}>
          Confirm the sheet and header row for {file.name}
        </p>
        <p style={{ fontSize: T.fsXs5, color: T.mid, marginBottom: 16, marginTop: 0 }}>
          We auto-detected the sheet and header row below - override either if it doesn&apos;t look right.
        </p>

        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 16 }}>
          {sheetNames.length > 1 && (
            <label style={{ fontSize: T.fsSm5, fontWeight: T.fwSemibold, color: T.dark }}>
              Sheet{" "}
              <select
                data-testid="xlsx-sheet-select"
                value={sheetName}
                onChange={(e) => changeXlsxSheet(e.target.value)}
                style={{
                  marginLeft: 6,
                  fontWeight: T.fwSemibold,
                  fontSize: T.fsSm5,
                  padding: "3px 6px",
                  borderRadius: 5,
                  border: `1px solid ${T.border}`,
                  background: T.white,
                  color: T.dark,
                }}
              >
                {sheetNames.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </label>
          )}

          <label style={{ fontSize: T.fsSm5, fontWeight: T.fwSemibold, color: T.dark }}>
            Header row{" "}
            <select
              data-testid="xlsx-header-row-select"
              value={headerRowIndex}
              onChange={(e) => changeXlsxHeaderRow(Number(e.target.value))}
              style={{
                marginLeft: 6,
                fontWeight: T.fwSemibold,
                fontSize: T.fsSm5,
                padding: "3px 6px",
                borderRadius: 5,
                border: `1px solid ${T.border}`,
                background: T.white,
                color: T.dark,
              }}
            >
              {rowOptions.map((row, idx) => {
                const preview = row.filter((v) => v !== null && v !== undefined && String(v).trim() !== "")
                  .slice(0, 3).join(", ");
                return (
                  <option key={idx} value={idx}>
                    Row {idx + 1}{preview ? ` — ${preview}` : " — (blank)"}
                  </option>
                );
              })}
            </select>
          </label>
        </div>

        <div
          data-testid="xlsx-column-preview"
          style={{
            border: `1px solid ${T.border}`,
            background: T.pageBg,
            borderRadius: 9,
            padding: "11px 12px",
            marginBottom: 16,
          }}
        >
          <div
            style={{
              fontSize: T.fsXxs,
              fontWeight: T.fwBold,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              color: T.mid,
              marginBottom: 7,
            }}
          >
            Columns this header produces
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {columnNames.map((name, i) => {
              const isKeyMapped = XLSX_KEY_MAPPED_COLUMNS.some(
                (key) => key.toLowerCase() === name.toLowerCase()
              );
              return (
                <span
                  key={`${name}-${i}`}
                  data-testid={`xlsx-column-chip-${name}`}
                  style={{
                    fontSize: T.fsXs5,
                    fontWeight: T.fwSemibold,
                    color: isKeyMapped ? T.blue : T.dark,
                    background: isKeyMapped ? T.blueSoft : T.white,
                    border: `1px solid ${isKeyMapped ? T.blueSoftBorder : T.border}`,
                    borderRadius: 6,
                    padding: "3px 8px",
                  }}
                >
                  {name}
                </span>
              );
            })}
          </div>
          <div
            data-testid="xlsx-row-count-line"
            style={{ fontSize: T.fsXs5, color: T.mid, fontWeight: T.fwSemibold, marginTop: 9 }}
          >
            {columnNames.length} columns · {dataRowCount} data rows detected
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <button
            type="button"
            style={{ ...S.btnBase, ...S.btnPrimary }}
            data-testid="xlsx-confirm-btn"
            onClick={confirmXlsxSheet}
          >
            Use this sheet →
          </button>
          <button
            type="button"
            style={{ ...S.btnBase, ...S.btnSecondary }}
            data-testid="xlsx-cancel-btn"
            onClick={cancelXlsxStage}
          >
            Upload a different file
          </button>
        </div>
      </div>
    );
  }

  // ── Post-upload confirmation ──────────────────────────────────────────────
  if (staged) {
    const { result, stagedFiles } = staged;
    const isPdf         = result.detectedSourceType === "pdf_abstraction";
    const profileNeeded = !!result.profileNeeded;
    const firstFile     = stagedFiles?.[0];

    return (
      <div>
        <p style={{ fontSize: T.fsBase, color: T.dark, marginBottom: 4, fontWeight: T.fwSemibold }}>
          We found your file. Here&apos;s what we detected - confirm to continue.
        </p>

        {/* PDF notice */}
        {isPdf && (
          <div
            style={{
              padding: "9px 14px",
              borderRadius: 6,
              background: T.blueSoft,
              border: `1px solid ${T.blueDeep}`,
              fontSize: T.fsSm5,
              color: T.blueDeep,
              fontWeight: T.fwSemibold,
              marginBottom: 12,
            }}
            data-testid="pdf-abstraction-notice"
          >
            PDF lease abstract - column mapping is handled automatically. You&apos;ll go directly to Validate.
          </div>
        )}

        {/* Profile-needed notice */}
        {profileNeeded && !isPdf && (
          <div
            style={{
              padding: "9px 14px",
              borderRadius: 6,
              background: T.warnAmberBg,
              border: `1px solid ${T.utilAmberBorder}`,
              fontSize: T.fsSm5,
              color: T.warnAmberText,
              marginBottom: 12,
            }}
            data-testid="profile-needed-notice"
          >
            <strong>Profile needed:</strong> no matching import profile was found for this file. You&apos;ll be able to configure column mappings in the next step.
          </div>
        )}

        {/* INGEST-PIPELINE-TYPE-AWARE-S1: detected data type + override ─────── */}
        {!isPdf && Array.isArray(result.availableDataTypes) && result.availableDataTypes.length > 0 && (
          <div
            style={{
              padding: "12px 16px",
              borderRadius: 6,
              background: result.ambiguousDataType && !dataTypeOverride ? T.warnAmberBg : T.pageBg,
              border: `1px solid ${result.ambiguousDataType && !dataTypeOverride ? T.utilAmberBorder : T.border}`,
              marginBottom: 16,
            }}
            data-testid="data-type-detection-panel"
          >
            <label
              htmlFor="data-type-select"
              style={{ display: "block", fontSize: T.fsSm5, fontWeight: T.fwSemibold, color: T.dark, marginBottom: 4 }}
            >
              Detected data type:{" "}
              <select
                id="data-type-select"
                data-testid="data-type-select"
                value={
                  result.ambiguousDataType && !dataTypeOverride
                    ? ""
                    : dataTypeOverride ?? result.detectedDataType ?? ""
                }
                disabled={retyping}
                onChange={async (e) => {
                  const nextType = e.target.value;
                  setDataTypeOverride(nextType);
                  if (nextType === result.detectedDataType) {
                    setRetypeFailed(false);
                    return;
                  }
                  setRetyping(true);
                  setRetypeFailed(false);
                  setError(null);
                  try {
                    const nextResult = await stageImportFile(firstFile, { overrideDataType: nextType });
                    setStaged({ result: nextResult, stagedFiles });
                    setRetypeFailed(false);
                  } catch (err) {
                    setError(err.message || "Could not re-detect this data type");
                    setRetypeFailed(true);
                  } finally {
                    setRetyping(false);
                  }
                }}
                style={{
                  marginLeft: 6,
                  fontWeight: T.fwSemibold,
                  fontSize: T.fsSm5,
                  padding: "3px 6px",
                  borderRadius: 5,
                  border: `1px solid ${T.border}`,
                  background: T.white,
                  color: T.dark,
                }}
              >
                {result.ambiguousDataType && !dataTypeOverride && (
                  <option value="" disabled> - Select a data type - </option>
                )}
                {result.availableDataTypes.map((dt) => (
                  <option key={dt.key} value={dt.key}>{dt.label}</option>
                ))}
              </select>
            </label>
            <p style={{ margin: "4px 0 0 0", fontSize: T.fsXs, color: T.mid }} data-testid="data-type-reason">
              {result.ambiguousDataType && !dataTypeOverride
                ? "We couldn't confidently detect a data type for this file - please choose one to continue."
                : result.dataTypeReason}
            </p>
          </div>
        )}

        <div
          style={{
            border: `1px solid ${T.border}`,
            borderRadius: 8,
            padding: "16px 20px",
            background: T.pageBg,
            marginBottom: 16,
            fontSize: T.fsSm5,
          }}
          data-testid="upload-detection-summary"
        >
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
            <div>
              <span style={{ color: T.mid }}>
                {stagedFiles?.length > 1 ? "Documents" : "File"}
              </span>
              <div style={{ fontWeight: T.fwSemibold, color: T.dark, marginTop: 2 }}>
                {stagedFiles?.length > 1
                  ? `${stagedFiles.length} PDF documents`
                  : firstFile?.name ?? result.filename ?? "leases_export.csv"}
              </div>
            </div>
            {result.suggestedProfileName && (
              <div>
                <span style={{ color: T.mid }}>Profile auto-detected</span>
                <div
                  style={{
                    fontWeight: T.fwSemibold,
                    color: T.statusDoneText,
                    marginTop: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                  data-testid="detected-profile-badge"
                >
                  ✓ {result.suggestedProfileName}
                </div>
              </div>
            )}
            {!isPdf && result.preview ? (
              <div>
                <span style={{ color: T.mid }}>Records found</span>
                <div style={{ fontWeight: T.fwSemibold, color: T.dark, marginTop: 2 }}>
                  {result.preview.totalRows ?? result.rowCount ?? 0}
                </div>
              </div>
            ) : !isPdf ? (
              <div>
                <span style={{ color: T.mid }}>Rows staged</span>
                <div style={{ fontWeight: T.fwSemibold, color: T.dark, marginTop: 2 }}>
                  {result.rowCount ?? 0}
                </div>
              </div>
            ) : null}
            {!isPdf && (
              <div>
                <span style={{ color: T.mid }}>Columns</span>
                <div style={{ fontWeight: T.fwSemibold, color: T.dark, marginTop: 2 }}>
                  {result.columnCount ?? result.columnMappings?.length ?? "—"}
                </div>
              </div>
            )}
            {isPdf && result.extractedFieldCount != null && (
              <div>
                <span style={{ color: T.mid }}>Fields extracted</span>
                <div style={{ fontWeight: T.fwSemibold, color: T.dark, marginTop: 2 }}>
                  {result.extractedFieldCount}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* HEADCOUNT-IMPORT-S1-S4 Slice 1: scrollable preview table for headcount sessions */}
        {!isPdf && result.preview?.rows?.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <p style={{ margin: "0 0 6px", fontSize: T.fsXs5, color: T.collectionCount }}>
              {result.preview.totalRows > 100
                ? `Showing 100 of ${result.preview.totalRows} office records`
                : `Showing all ${result.preview.totalRows} office record${result.preview.totalRows !== 1 ? "s" : ""}`}
            </p>
            <div
              data-testid="headcount-preview-table"
              style={{
                overflowX: "auto",
                overflowY: "auto",
                maxHeight: 260,
                border: `1px solid ${T.border}`,
                borderRadius: 6,
              }}
            >
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: T.fsXs5,
                }}
              >
                <thead>
                  <tr style={{ background: T.pageBg, position: "sticky", top: 0 }}>
                    {Object.keys(result.preview.rows[0]).map((col) => (
                      <th
                        key={col}
                        style={{
                          padding: "5px 10px",
                          textAlign: "left",
                          fontWeight: T.fwSemibold,
                          color: T.mid,
                          borderBottom: `1px solid ${T.border}`,
                          whiteSpace: "nowrap",
                          fontSize: T.fsXs,
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                        }}
                      >
                        {HEADCOUNT_LABELS[col] ?? col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.preview.rows.map((row, i) => (
                    <tr
                      key={i}
                      style={{ borderBottom: `1px solid ${T.border}`, background: i % 2 === 0 ? T.card : T.pageBg }}
                    >
                      {Object.values(row).map((val, j) => (
                        <td
                          key={j}
                          style={{
                            padding: "5px 10px",
                            color: T.dark,
                            fontFamily: j === 0 ? "monospace" : undefined,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {val ?? "—"}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Location picker — required for PDF sessions */}
        {isPdf && (
          <div style={{ marginBottom: 16 }}>
            <label
              htmlFor="pdf-location-picker"
              style={{
                display:      "block",
                fontSize:     T.fsSm5,
                fontWeight:   T.fwSemibold,
                color:        T.dark,
                marginBottom: 6,
              }}
            >
              Which location are these documents for?
              <span style={{ color: T.errorText, marginLeft: 3 }}>*</span>
            </label>
            <select
              id="pdf-location-picker"
              data-testid="pdf-location-picker"
              value={selectedLocationId}
              onChange={(e) => {
                setSelectedLocationId(e.target.value);
                setLocationSuggested(false);
              }}
              style={{
                width:        "100%",
                maxWidth:     480,
                padding:      "7px 10px",
                fontSize:     T.fsSm5,
                border:       `1px solid ${selectedLocationId ? T.border : T.errorBorder ?? T.utilAmberBorder}`,
                borderRadius: 6,
                background:   T.white,
                color:        T.dark,
                appearance:   "auto",
              }}
            >
              <option value=""> - Select a location - </option>
              {locationOptions.map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {buildLocationLabel(loc)}
                </option>
              ))}
            </select>
            {locationSuggested && selectedLocationId && (
              <div
                style={{
                  marginTop:  5,
                  fontSize:   T.fsXs,
                  color:      T.statusDoneText,
                  fontWeight: T.fwSemibold,
                }}
                data-testid="location-suggested-badge"
              >
                ✓ Suggested from filename - confirm or change
              </div>
            )}
            {!selectedLocationId && (
              <div
                style={{ marginTop: 5, fontSize: T.fsXs, color: T.mid }}
                data-testid="location-required-hint"
              >
                A location is required to continue.
              </div>
            )}
          </div>
        )}

        {/* Selection required before Confirm & Continue when detection is ambiguous. */}
        {!isPdf && result.ambiguousDataType && !dataTypeOverride && (
          <div
            style={{ marginBottom: 10, fontSize: T.fsXs, color: T.warnAmberText, fontWeight: T.fwSemibold }}
            data-testid="data-type-selection-required"
          >
            Choose a data type above before continuing.
          </div>
        )}

        <div style={{ display: "flex", gap: 10, alignItems: "center", justifyContent: "space-between" }}>
          <button
            type="button"
            style={{ ...S.btnBase, ...S.btnSecondary }}
            onClick={resetAll}
            data-testid="upload-different-btn"
          >
            Upload a different file
          </button>
          <button
            type="button"
            style={{
              ...S.btnBase,
              ...S.btnPrimary,
              ...((isPdf && !selectedLocationId) || (!isPdf && result.ambiguousDataType && !dataTypeOverride) || retyping || retypeFailed
                ? { opacity: 0.5, cursor: "not-allowed" }
                : {}),
            }}
            disabled={
              locationAssigning ||
              retyping ||
              retypeFailed ||
              (isPdf && !selectedLocationId) ||
              (!isPdf && result.ambiguousDataType && !dataTypeOverride)
            }
            onClick={async () => {
              if (isPdf && result.sessionId && selectedLocationId) {
                setLocationAssigning(true);
                try {
                  await assignSessionLocation(result.sessionId, selectedLocationId);
                } catch {
                  // non-fatal — validator will surface the identifier error if
                  // the assign call fails; the user can re-select and retry
                } finally {
                  setLocationAssigning(false);
                }
              }
              if (!isPdf && result.sessionId && result.detectedDataType) {
                try {
                  await confirmDataType(result.sessionId, dataTypeOverride ?? result.detectedDataType);
                } catch (err) {
                  setError(err.message || "Could not confirm the data type");
                  return;
                }
              }
              // Stamp confirmedDataType so MapStep reads it as an unambiguous
              // single source of truth rather than falling through its own
              // fallback chain.  result is the staged response object — mutating
              // it here is safe (it is a local copy, not shared state).
              result.confirmedDataType = dataTypeOverride ?? result.detectedDataType;
              onComplete(result, firstFile);
            }}
            data-testid="confirm-and-continue-btn"
          >
            {locationAssigning ? "Saving…" : "Confirm & Continue →"}
          </button>
        </div>
      </div>
    );
  }

  // ── Already-staged result (returning from step 2) ────────────────────────
  if (existingResult && pdfFiles.length === 0 && !csvFile) {
    return (
      <div>
        <p style={{ fontSize: T.fsBase, color: T.dark, marginBottom: 4, fontWeight: T.fwSemibold }}>
          You have a file already staged - continue or swap it out.
        </p>
        <div
          style={{
            border: `1px solid ${T.statusDoneBorder}`,
            borderRadius: 8,
            padding: "16px 20px",
            background: T.statusDoneBg,
            color: T.statusDoneText,
            marginBottom: 16,
            fontSize: T.fsSm5,
          }}
          data-testid="existing-staged-notice"
        >
          ✓ <strong>{existingResult.filename ?? "leases_export.csv"}</strong>
          {" — "}
          {existingResult.rowCount} row{existingResult.rowCount !== 1 ? "s" : ""} staged
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <button
            type="button"
            style={{ ...S.btnBase, ...S.btnPrimary }}
            onClick={() => onComplete(existingResult, null)}
            data-testid="continue-existing-btn"
          >
            Continue with this file →
          </button>
          <button
            type="button"
            style={{ ...S.btnBase, ...S.btnSecondary }}
            onClick={onClearExisting}
            data-testid="upload-different-btn"
          >
            Upload a different file
          </button>
        </div>
      </div>
    );
  }

  // ── Drop zone (idle or PDF list building) ────────────────────────────────
  return (
    <div>
      <p style={{ fontSize: T.fsBase, color: T.dark, marginBottom: 6, fontWeight: T.fwSemibold }}>
        Upload your file - CSV or PDF. We&apos;ll detect the source automatically.
      </p>
      <p style={{ fontSize: T.fsXs5, color: T.mid, marginBottom: 18, marginTop: 0 }}>
        Accepted formats: CSV or PDF lease abstract. Drag multiple PDFs for multi-document synthesis.
        Maximum 10 MB per file. Files are staged for review before anything changes.
      </p>

      <div
        style={dropStyle}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => fileRef.current?.click()}
        role="button"
        tabIndex={0}
        data-testid="upload-drop-zone"
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") fileRef.current?.click(); }}
      >
        <div style={{ position: "relative", width: 48, height: 48, margin: "0 auto 8px" }}>
          <span
            data-testid="upload-cloud-icon"
            style={{
              position:   "absolute",
              inset:      0,
              display:    "flex",
              alignItems: "center",
              justifyContent: "center",
              opacity:    uploadPhase !== null ? 0 : 1,
              transition: "opacity 150ms",
            }}
          >
            <UploadCloud size={30} color={T.light} strokeWidth={1.7} aria-hidden="true" />
          </span>
          <svg
            data-testid="upload-progress-ring"
            viewBox="0 0 48 48"
            width={48}
            height={48}
            style={{
              position: "absolute",
              inset:    0,
              opacity:  uploadPhase !== null ? 1 : 0,
              transition: "opacity 150ms",
              animation: "upload-ring-spin 0.9s linear infinite",
            }}
            aria-hidden="true"
          >
            <circle cx="24" cy="24" r="18" fill="none" stroke={T.border} strokeWidth="3" />
            <circle
              cx="24" cy="24" r="18" fill="none"
              stroke={T.editorBlue} strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray="85 113"
              strokeDashoffset="28"
            />
          </svg>
        </div>
        <p style={{ margin: 0, fontSize: T.fsSm5, color: T.mid }}>
          {uploadPhase === "uploading"
            ? "Uploading…"
            : uploadPhase === "abstracting"
            ? "Abstracting…"
            : pdfFiles.length > 0
            ? `${pdfFiles.length} PDF document${pdfFiles.length !== 1 ? "s" : ""} ready - drag more or click to add`
            : "Drag a CSV or PDF here, or click to browse"}
        </p>
        <input
          ref={fileRef}
          type="file"
          accept=".csv,.xlsx,.pdf"
          multiple
          style={{ display: "none" }}
          onChange={onInputChange}
          data-testid="upload-file-input"
        />
      </div>

      {/* PDF file list with remove buttons */}
      {pdfFiles.length > 0 && (
        <div
          data-testid="pdf-file-list"
          style={{
            marginTop: 12,
            border: `1px solid ${T.border}`,
            borderRadius: 7,
            overflow: "hidden",
          }}
        >
          {pdfFiles.map((f) => (
            <div
              key={f.name}
              data-testid={`pdf-file-item-${f.name}`}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "8px 14px",
                borderBottom: `1px solid ${T.border}`,
                fontSize: T.fsSm5,
                background: T.pageBg,
              }}
            >
              <span style={{ fontSize: T.fsBase }}>📄</span>
              <span
                style={{
                  flex: 1, color: T.dark,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}
              >
                {f.name}
              </span>
              <span style={{ color: T.mid, fontSize: T.fsXs, flexShrink: 0 }}>
                {(f.size / 1024).toFixed(0)} KB
              </span>
              <button
                type="button"
                data-testid={`pdf-remove-btn-${f.name}`}
                onClick={() => removePdfFile(f.name)}
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  color: T.mid, fontSize: T.fsBase, padding: "0 2px",
                  lineHeight: 1, flexShrink: 0,
                }}
                title={`Remove ${f.name}`}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Abstract button for multi-file PDF */}
      {pdfFiles.length > 0 && (
        <div style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "center" }}>
          <button
            type="button"
            style={{
              ...S.btnBase,
              ...S.btnPrimary,
              opacity: busy ? 0.5 : 1,
            }}
            data-testid="abstract-pdfs-btn"
            onClick={uploadPdfFiles}
            disabled={busy}
          >
            {busy
              ? "Abstracting…"
              : `Abstract ${pdfFiles.length} document${pdfFiles.length !== 1 ? "s" : ""} →`}
          </button>
          <button
            type="button"
            style={{ ...S.btnBase, ...S.btnSecondary }}
            data-testid="clear-pdf-list-btn"
            onClick={() => setPdfFiles([])}
            disabled={busy}
          >
            Clear all
          </button>
        </div>
      )}

      {error && (
        <div
          role="alert"
          style={{
            marginTop: 12,
            padding: "8px 12px",
            borderRadius: 6,
            background: T.utilRed,
            color: T.red,
            fontSize: T.fsSm5,
          }}
          data-testid="upload-error"
        >
          {error}
        </div>
      )}
    </div>
  );
}

// ─── Step 2: Map ──────────────────────────────────────────────────────────────
//
// INGEST-MAP-GROUPED: Four reason-based accordion sections replace the flat
// column table.  Profile-aware collapse hides sections 2 and 3 when a known
// profile already covers the upload (state b).

// Shared grid template applied to every row type so all four sections' columns
// line up identically: source name | arrow | target/badge/hint | action control.
const MAP_ROW_GRID = "minmax(0,1fr) 20px minmax(0,1fr) auto";

// Reason-derivation is server-side only. The UI offers a single plain "Ignore"
// action; the server classifies the reason from context at apply time.
// HEADCOUNT-IMPORT-S1-S4: replaced 5-option sub-menu with single "Ignore".
const IGNORE_ACTION = { reason: "low-value", reasonLabel: "Ignored" };

// Small collapsible section wrapper used by all four Map sections.
// badgeVariant="warning" switches the count badge to amber colours.
// badgeLabel overrides the badge text (defaults to the numeric count).
// description is an always-visible one-liner shown below the heading.
function MapSection({ title, description, count, open, onToggle, children, testId, alwaysOpen, badgeVariant, badgeLabel, accentBorder }) {
  const badgeBg    = badgeVariant === "warning" ? T.warnAmberBg   : T.lightGray;
  const badgeColor = badgeVariant === "warning" ? T.warnAmberText : T.mid;
  return (
    <div
      style={{
        border: `1px solid ${T.border}`,
        borderLeft: accentBorder ? `3px solid ${accentBorder}` : `1px solid ${T.border}`,
        borderRadius: 7,
        marginBottom: 10,
        overflow: "hidden",
      }}
      data-testid={testId}
    >
      <button
        type="button"
        title={description}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "flex-start",
          flexDirection: "column",
          gap: 2,
          padding: "10px 14px",
          background: T.pageBg,
          border: "none",
          cursor: alwaysOpen ? "default" : "pointer",
          textAlign: "left",
          fontSize: T.fsSm5,
        }}
        onClick={alwaysOpen ? undefined : onToggle}
        aria-expanded={open}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%" }}>
          <span style={{ fontWeight: T.fwSemibold, color: T.dark, flex: 1 }}>{title}</span>
          <span
            data-testid={testId ? `${testId}-badge` : undefined}
            style={{
              padding: "1px 8px",
              borderRadius: 10,
              background: badgeBg,
              color: badgeColor,
              fontSize: T.fsXs,
              fontWeight: T.fwSemibold,
            }}
          >
            {badgeLabel ?? count}
          </span>
          {!alwaysOpen && (
            <span style={{ color: T.mid, fontSize: T.fsXs }}>{open ? "▲" : "▼"}</span>
          )}
        </div>
        {description && (
          <span style={{ color: T.mid, fontSize: T.fsXs5, fontWeight: T.fwRegular }}>
            {description}
          </span>
        )}
      </button>
      {open && (
        <div style={{ borderTop: `1px solid ${T.border}`, background: T.card }}>
          {count > 0 && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: MAP_ROW_GRID,
                gap: 8,
                padding: "4px 14px",
                borderBottom: `1px solid ${T.border}`,
                fontSize: T.fsXs,
                color: T.light,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                fontWeight: T.fwSemibold,
              }}
            >
              <span>Source column</span>
              <span />
              <span>Reason</span>
              <span>Action</span>
            </div>
          )}
          {children}
        </div>
      )}
    </div>
  );
}

// Row used in the "Mapped" section.  Shows CSV column → WorkplaceOS field with
// a Change dropdown.
// Returns a short badge label for load-bearing canonical fields, null otherwise.
function badgeForCanonical(canonicalField) {
  if (!canonicalField) return null;
  if (canonicalField === "location_id" || canonicalField === "source_location_name") {
    return "match key";
  }
  if (canonicalField === "currency") return "controls cost schedule";
  if (/^_yc_\d{4}_(rent|opex|capex)$/i.test(canonicalField)) return "controls cost schedule";
  return null;
}

function MappedRow({ item, onChangeField, mappedCanonicals = new Set(), dataTypeKey, badge }) {
  const [editing, setEditing] = useState(false);
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: MAP_ROW_GRID,
        alignItems: "center",
        gap: 8,
        padding: "7px 14px",
        borderBottom: `1px solid ${T.border}`,
        fontSize: T.fsXs5,
      }}
    >
      <span style={{ color: T.dark, fontFamily: "monospace", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.csvColumn}</span>
      <span style={{ color: T.mid, fontSize: T.fsXs, textAlign: "center" }}>→</span>
      {editing ? (
        <select
          value={item.canonicalField ?? ""}
          onChange={(e) => {
            onChangeField(item.csvColumn, e.target.value || null);
            setEditing(false);
          }}
          autoFocus
          data-testid={`mapped-change-select-${item.csvColumn}`}
          style={{
            padding: "3px 6px",
            borderRadius: 4,
            border: `1px solid ${T.border}`,
            fontSize: T.fsXs5,
            color: T.dark,
            background: T.card,
            minWidth: 0,
            gridColumn: "3 / 5",
          }}
        >
          <option value=""> - Unmapped - </option>
          {canonicalFieldsForDataType(dataTypeKey).map((f) => {
            const alreadyMapped = f !== item.canonicalField && mappedCanonicals.has(f);
            return (
              <option key={f} value={f} disabled={alreadyMapped}>
                {canonicalLabel(f)}{alreadyMapped ? " (already mapped)" : ""}
              </option>
            );
          })}
        </select>
      ) : (
        <>
          <span
            style={{
              color: T.dark,
              fontFamily: "monospace",
              fontSize: T.fsXs5,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            {item.canonicalField ?? "—"}
            {badge && (
              <span
                data-testid={`mapped-badge-${item.csvColumn}`}
                style={{
                  display: "inline-block",
                  padding: "1px 6px",
                  borderRadius: 8,
                  fontSize: T.fsXs,
                  fontWeight: T.fwSemibold,
                  background: T.lightGray,
                  color: T.mid,
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                {badge}
              </span>
            )}
          </span>
          <button
            type="button"
            onClick={() => setEditing(true)}
            data-testid={`mapped-change-btn-${item.csvColumn}`}
            style={{
              fontSize: T.fsXs,
              padding: "2px 8px",
              borderRadius: 4,
              border: `1px solid ${T.border}`,
              background: "transparent",
              color: T.mid,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            Change
          </button>
        </>
      )}
    </div>
  );
}

// Row used in the "Not used" section.  Shows the reason chip and a "Map anyway"
// button.
function NotUsedRow({ item, onMapAnyway }) {
  const chipColors = {
    "owned-by-source":    { bg: T.warnAmberBg,   color: T.warnAmberText },
    "computed-by-us":     { bg: T.blueSoft,       color: T.blueDeep },
    "identity":           { bg: T.lightGray,      color: T.mid },
    "low-value":          { bg: T.lightGray,      color: T.mid },
    "local-currency-dup": { bg: T.lightGray,      color: T.mid },
  };
  const { bg, color } = chipColors[item.reason] ?? chipColors["low-value"];
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: MAP_ROW_GRID,
        alignItems: "center",
        gap: 8,
        padding: "7px 14px",
        borderBottom: `1px solid ${T.border}`,
        fontSize: T.fsXs5,
      }}
      data-testid={`not-used-row-${item.csvColumn}`}
    >
      <span style={{ color: T.dark, fontFamily: "monospace", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.csvColumn}</span>
      <span style={{ color: T.mid, fontSize: T.fsXs, textAlign: "center" }}>→</span>
      <span
        style={{
          padding: "2px 8px",
          borderRadius: 10,
          fontSize: T.fsXs,
          fontWeight: T.fwSemibold,
          background: bg,
          color,
          whiteSpace: "nowrap",
          justifySelf: "start",
        }}
        data-testid={`reason-chip-${item.csvColumn}`}
      >
        {item.reasonLabel ?? item.reason}
      </span>
      <button
        type="button"
        onClick={() => onMapAnyway(item.csvColumn)}
        data-testid={`map-anyway-btn-${item.csvColumn}`}
        style={{
          fontSize: T.fsXs,
          padding: "2px 8px",
          borderRadius: 4,
          border: `1px solid ${T.border}`,
          background: "transparent",
          color: T.mid,
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
      >
        Map anyway
      </button>
    </div>
  );
}

// Row used in the "Recognized — skipped" section.
// All "not-used" columns land here: currency duplicates, identity fields,
// low-value columns, not-tracked fields, etc.  Shows the per-row reason
// label and a "Map anyway" escape hatch.
function SkippedDuplicateRow({ item, onMapAnyway }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: MAP_ROW_GRID,
        alignItems: "center",
        gap: 8,
        padding: "7px 14px",
        borderBottom: `1px solid ${T.border}`,
        fontSize: T.fsXs5,
      }}
      data-testid={`skipped-duplicate-row-${item.csvColumn}`}
    >
      <span style={{ color: T.dark, fontFamily: "monospace", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.csvColumn}</span>
      <span style={{ color: T.mid, fontSize: T.fsXs, textAlign: "center" }}>→</span>
      <span
        style={{ color: T.mid, fontSize: T.fsXs5, minWidth: 0 }}
        data-testid={`reason-chip-${item.csvColumn}`}
      >
        {item.reasonLabel ?? item.reason}
      </span>
      <button
        type="button"
        onClick={() => onMapAnyway(item.csvColumn)}
        data-testid={`map-anyway-btn-${item.csvColumn}`}
        style={{
          fontSize: T.fsXs,
          padding: "2px 8px",
          borderRadius: 4,
          border: `1px solid ${T.border}`,
          background: "transparent",
          color: T.mid,
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
      >
        Map anyway
      </button>
    </div>
  );
}

// Row used in the "Unrecognized" section.  Single "Ignore" + Map dropdown.
// HEADCOUNT-IMPORT-S1-S4:
//   2a — controlled select so the selected label persists after selection
//   2b — replaced multi-reason "Ignore as" sub-menu with single "Ignore" option
function UnrecognizedRow({ item, onIgnore, onMap, mappedCanonicals = new Set(), dataTypeKey }) {
  const [selectedVal, setSelectedVal] = useState("");

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: MAP_ROW_GRID,
        alignItems: "center",
        gap: 8,
        padding: "7px 14px",
        borderBottom: `1px solid ${T.border}`,
        fontSize: T.fsXs5,
      }}
      data-testid={`unrecognized-row-${item.csvColumn}`}
    >
      <span style={{ color: T.dark, fontFamily: "monospace", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.csvColumn}</span>
      <span style={{ color: T.mid, fontSize: T.fsXs, textAlign: "center" }}>→</span>
      <span style={{ color: T.light, fontSize: T.fsXs, minWidth: 0 }}>
        Unknown column - leave ignored or map if you know its field.
      </span>
      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
        <select
          value={selectedVal}
          onChange={(e) => {
            const val = e.target.value;
            setSelectedVal(val);
            if (!val) return;
            if (val === "__ignore__") {
              onIgnore(item.csvColumn, IGNORE_ACTION.reason, IGNORE_ACTION.reasonLabel);
            } else {
              onMap(item.csvColumn, val);
            }
          }}
          data-testid={`map-select-${item.csvColumn}`}
          style={{
            padding: "3px 6px",
            borderRadius: 4,
            border: `1px solid ${T.border}`,
            fontSize: T.fsXs5,
            color: T.mid,
            background: T.card,
            minWidth: 140,
          }}
        >
          <option value="">Map to…</option>
          <option value="__ignore__">Ignore</option>
          <optgroup label="Map to canonical field">
            {canonicalFieldsForDataType(dataTypeKey).map((f) => (
              <option key={f} value={f} disabled={mappedCanonicals.has(f)}>
                {canonicalLabel(f)}{mappedCanonicals.has(f) ? " (already mapped)" : ""}
              </option>
            ))}
          </optgroup>
        </select>
      </div>
    </div>
  );
}

// Row used in the "Need your eyes" section.  Displays a match-key, currency,
// or year-cost-matrix column and its current canonical mapping.
function NeedsEyesRow({ item }) {
  const labelMap = {
    leaseIdSpacebase:   "Match key (Lease ID)",
    _rippleLocationId:  "Match key (Location ID)",
    currency:           "Pinned currency",
  };
  const hintMap = {
    leaseIdSpacebase:   "Match key - confirm",
    _rippleLocationId:  "Match key - confirm",
    currency:           "Pinned to avoid double-conversion",
  };
  const isYearCost = item.canonicalField
    ? /^_yc_\d{4}_(rent|opex|capex)$/i.test(item.canonicalField)
    : false;
  const typeLabel = isYearCost
    ? "Year cost matrix"
    : (labelMap[item.canonicalField] ?? "Decision item");
  const hint = isYearCost
    ? "Feeds the per-year rent schedule"
    : (hintMap[item.canonicalField] ?? "Review and confirm");

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: MAP_ROW_GRID,
        alignItems: "center",
        gap: 8,
        padding: "7px 14px",
        borderBottom: `1px solid ${T.border}`,
        fontSize: T.fsXs5,
      }}
      data-testid={`needs-eyes-row-${item.csvColumn}`}
    >
      <span style={{ color: T.dark, fontFamily: "monospace", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.csvColumn}</span>
      <span style={{ color: T.mid, fontSize: T.fsXs, textAlign: "center" }}>→</span>
      <div style={{ minWidth: 0 }}>
        <span
          style={{
            display: "inline-block",
            padding: "2px 8px",
            borderRadius: 10,
            fontSize: T.fsXs,
            fontWeight: T.fwSemibold,
            background: T.statusDoneBg,
            color: T.statusDoneText,
            whiteSpace: "nowrap",
            marginBottom: 2,
          }}
        >
          {typeLabel}
        </span>
        <div style={{ color: T.light, fontSize: T.fsXs }}>
          {hint}
        </div>
      </div>
      <span style={{ color: T.mid, fontSize: T.fsXs, fontFamily: "monospace", whiteSpace: "nowrap" }}>
        {item.canonicalField}
      </span>
    </div>
  );
}

// INGEST-SUMMARY-SCREEN-S1: shared serialisation used both by the Step 2
// "Save changes to profile" button and by the Step 5 "Save mapping as
// profile" flow (for sessions where the user skipped saving at Step 2).
function buildProfilePayloadFromItems(items) {
  const column_map   = {};
  const dispositions = {};
  items.forEach(({ csvColumn, section, canonicalField, reason, reasonLabel }) => {
    if (canonicalField && section !== "not-used") {
      column_map[csvColumn] = canonicalField;
    }
    if (section === "not-used" && reason) {
      dispositions[csvColumn] = { action: "ignore", reason, reasonLabel: reasonLabel ?? reason };
    } else if (section === "mapped" && canonicalField) {
      dispositions[csvColumn] = { action: "map", canonicalField };
    }
  });
  return { column_map, dispositions };
}

export function MapStep({ stageResult, onComplete, onBack, isAdmin, legacyOverride, onLegacyOverrideChange, onNeedsEyesChange, onMappingSnapshotChange }) {
  // INGEST-PIPELINE-TYPE-AWARE-S1: which registered data type this session was
  // confirmed/detected as ("leases" | "badging"). Drives which canonical field
  // list is offered in the mapping dropdowns.
  const dataTypeKey = stageResult?.confirmedDataType ?? stageResult?.detectedDataType ?? "leases";
  // items: per-column state — section assignments drive which section a column
  // appears in; user actions update this array in place.
  //
  // Smart-confirm: high-confidence needs-eyes items (match-key, currency pin,
  // year-cost matrix) are unambiguously resolved and are auto-accepted at
  // initialisation — moved straight to "mapped" so only genuinely ambiguous
  // rows remain in the confirmation list.
  const [items, setItems] = useState(() =>
    (stageResult?.columnMappings ?? []).map((item) =>
      item.section === "needs-eyes" && item.confidence === "high"
        ? { ...item, section: "mapped" }
        : item,
    ),
  );

  const [saving, setSaving]           = useState(false);
  const [saveError, setSaveError]     = useState(null);
  const [saved, setSaved]             = useState(false);
  const [profileName, setProfileName] = useState(
    stageResult?.suggestedProfileName ?? "My Profile",
  );
  const [showSave, setShowSave]       = useState(false);

  // INGEST-PIPELINE-TYPE-AWARE-S1: Profiles list — name, type, mappings,
  // last-used, edit/delete. Fetched independently of the current session so
  // the list reflects every saved profile, not just the matching one.
  const [profiles,        setProfiles]        = useState([]);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [profilesError,   setProfilesError]   = useState(null);
  const [deletingProfileId, setDeletingProfileId] = useState(null);
  const [showProfilesList, setShowProfilesList]   = useState(false);

  const refreshProfiles = useCallback(() => {
    setProfilesLoading(true);
    setProfilesError(null);
    getSourceProfiles()
      .then((rows) => setProfiles(Array.isArray(rows) ? rows : []))
      .catch((err) => setProfilesError(err.message || "Could not load profiles"))
      .finally(() => setProfilesLoading(false));
  }, []);

  useEffect(() => {
    if (showProfilesList) refreshProfiles();
  }, [showProfilesList, refreshProfiles]);

  const handleEditProfile = useCallback((profile) => {
    // Loads the saved column_map into the current mapping editor so the user
    // can adjust it and re-save under the same name.
    setProfileName(profile.name);
    setItems((prev) =>
      prev.map((item) => {
        const mapped = profile.column_map?.[item.csvColumn];
        if (mapped) return { ...item, section: "mapped", canonicalField: mapped, confidence: "high" };
        const disp = profile.dispositions?.[item.csvColumn];
        if (disp?.action === "ignore") {
          return { ...item, section: "not-used", reason: disp.reason, reasonLabel: disp.reasonLabel ?? disp.reason, canonicalField: null };
        }
        return item;
      }),
    );
    setShowAllSections(true);
    setShowSave(true);
    setShowProfilesList(false);
  }, []);

  const handleDeleteProfile = useCallback(async (profileId) => {
    setDeletingProfileId(profileId);
    try {
      await deleteSourceProfile(profileId);
      setProfiles((prev) => prev.filter((p) => p.id !== profileId));
    } catch (err) {
      setProfilesError(err.message || "Could not delete profile");
    } finally {
      setDeletingProfileId(null);
    }
  }, []);

  // State (b): profile already covers this upload — collapse sections 2 and 3
  // and show the profile banner instead.  User can toggle the full grid back.
  const hasProfile      = !!stageResult?.sourceProfile;

  // Dirty-state tracking for the hasProfile branch.
  // savedSnapshot captures the last-saved (or initial) item assignments.
  // Stored as state so updating it after a save triggers a re-render.
  const [savedSnapshot, setSavedSnapshot] = useState(() =>
    (stageResult?.columnMappings ?? []).map((item) => ({
      csvColumn:      item.csvColumn,
      section:        item.section === "needs-eyes" && item.confidence === "high" ? "mapped" : item.section,
      canonicalField: item.canonicalField ?? null,
      reason:         item.reason ?? null,
    })),
  );

  const isDirty = useMemo(() => {
    if (!hasProfile) return true;
    if (items.length !== savedSnapshot.length) return true;
    const snapMap = new Map(savedSnapshot.map((s) => [s.csvColumn, s]));
    return items.some((item) => {
      const s = snapMap.get(item.csvColumn);
      if (!s) return true;
      return (
        item.section        !== s.section        ||
        (item.canonicalField ?? null) !== s.canonicalField ||
        (item.reason        ?? null) !== s.reason
      );
    });
  }, [items, hasProfile, savedSnapshot]);
  const [showAllSections, setShowAllSections] = useState(false);
  const showFull        = !hasProfile || showAllSections;

  // Open/close state for each section.
  const [open, setOpen] = useState({
    "needs-eyes":    false,
    "mapped":        false,
    "not-used":      false,
    "unrecognized":  true,
  });
  const toggleOpen = (id) => setOpen((prev) => ({ ...prev, [id]: !prev[id] }));

  // Move a column to a different section.
  const moveItem = useCallback((csvColumn, updates) => {
    setItems((prev) =>
      prev.map((item) => item.csvColumn === csvColumn ? { ...item, ...updates } : item),
    );
  }, []);

  // Handlers for user actions.
  const handleMapAnyway = useCallback((csvColumn) => {
    moveItem(csvColumn, { section: "mapped" });
    setOpen((prev) => ({ ...prev, "mapped": true }));
  }, [moveItem]);

  const handleIgnore = useCallback((csvColumn, reason, reasonLabel) => {
    moveItem(csvColumn, { section: "not-used", reason, reasonLabel, canonicalField: null });
    setOpen((prev) => ({ ...prev, "skipped-duplicate": true }));
  }, [moveItem]);

  const handleMap = useCallback((csvColumn, canonicalField) => {
    moveItem(csvColumn, { section: "mapped", canonicalField, confidence: "high" });
    setOpen((prev) => ({ ...prev, "mapped": true }));
  }, [moveItem]);

  const handleChangeField = useCallback((csvColumn, canonicalField) => {
    moveItem(csvColumn, { canonicalField, confidence: canonicalField ? "high" : null });
  }, [moveItem]);

  // Save profile: serialise current section assignments into dispositions.
  const handleSaveProfile = useCallback(async () => {
    if (!profileName.trim()) return;
    setSaving(true);
    setSaveError(null);
    try {
      const column_map  = {};
      const dispositions = {};

      items.forEach(({ csvColumn, section, canonicalField, reason, reasonLabel }) => {
        // column_map: all columns with a canonical field (mapped or needs-eyes)
        if (canonicalField && section !== "not-used") {
          column_map[csvColumn] = canonicalField;
        }
        // dispositions: user-set ignore and map-anyway decisions
        if (section === "not-used" && reason) {
          dispositions[csvColumn] = { action: "ignore", reason, reasonLabel: reasonLabel ?? reason };
        } else if (section === "mapped" && canonicalField) {
          dispositions[csvColumn] = { action: "map", canonicalField };
        }
      });

      await saveSourceProfile({
        name:        profileName.trim(),
        source_type: stageResult.detectedSourceType,
        column_map,
        dispositions,
      });
      // Reset saved snapshot so isDirty recomputes and button returns to disabled.
      setSavedSnapshot(items.map((item) => ({
        csvColumn:      item.csvColumn,
        section:        item.section,
        canonicalField: item.canonicalField ?? null,
        reason:         item.reason ?? null,
      })));
      setSaved(true);
      setShowSave(false);
    } catch (err) {
      setSaveError(err.message || "Could not save profile");
    } finally {
      setSaving(false);
    }
  }, [items, profileName, stageResult]);

  // Derived section arrays (computed from items state).
  const needsEyes       = items.filter((i) => i.section === "needs-eyes");
  const mapped          = items.filter((i) => i.section === "mapped");
  // Recognized — skipped: all not-used items in one bucket (identity, low-value,
  // duplicate-currency-conversion, not-tracked, etc.)  Replaces the old split
  // between "skipped-duplicate" and "Not used by WorkplaceOS".
  const skippedDuplicate = items.filter((i) => i.section === "not-used");
  // notUsed is always empty; the section is kept so its testId and state-b
  // visibility logic remain intact.
  const notUsed         = [];
  const unrecognized    = items.filter((i) => i.section === "unrecognized");

  // Set of canonical fields currently in the mapped bucket — used to grey out
  // already-taken fields in the Unrecognized "Map to…" and Mapped "Change" dropdowns.
  const mappedCanonicals = useMemo(
    () => new Set(mapped.map((i) => i.canonicalField).filter(Boolean)),
    [mapped],
  );

  // Auto-mapped count: derived from the original server result (not items state)
  // so it reflects what the profile did, not post-user-action changes.
  const autoMappedCount = (stageResult?.columnMappings ?? []).filter(
    (c) => c.section === "mapped",
  ).length;
  const totalCount = stageResult?.columnCount ?? (stageResult?.columnMappings ?? []).length;

  // Notify parent whenever the needs-eyes count changes (drives the status strip).
  useEffect(() => {
    onNeedsEyesChange?.(needsEyes.length);
  }, [needsEyes.length, onNeedsEyesChange]);

  // INGEST-SUMMARY-SCREEN-S1: lift the current mapping snapshot up to the
  // wizard so Step 5 can offer "Save mapping as profile" for sessions where
  // the user skipped saving here. `saved` covers both the state (a) and (b)
  // save flows above.
  useEffect(() => {
    onMappingSnapshotChange?.({
      items,
      hasProfile,
      saved,
      sourceType: stageResult?.detectedSourceType ?? null,
    });
  }, [items, hasProfile, saved, stageResult, onMappingSnapshotChange]);

  // Confirm all: move every needs-eyes item to mapped.
  const handleConfirmAll = useCallback(() => {
    setItems((prev) =>
      prev.map((item) =>
        item.section === "needs-eyes" ? { ...item, section: "mapped" } : item,
      ),
    );
    setOpen((prev) => ({ ...prev, "mapped": true }));
  }, []);

  return (
    <div>
      <p style={{ fontSize: T.fsBase, color: T.dark, marginBottom: 4, fontWeight: T.fwSemibold }}>
        Check the column mapping - confirm any pre-set choices, then continue.
      </p>
      <p style={{ fontSize: T.fsSm5, color: T.mid, marginBottom: 6, marginTop: 0 }}>
        {stageResult?.columnCount ?? items.length} columns detected from{" "}
        <strong>{stageResult?.detectedSourceType?.replace(/_/g, " ")}</strong>.
        {needsEyes.length + unrecognized.length > 0 && (
          <span
            data-testid="map-attention-count"
            style={{ marginLeft: 10, color: T.warnAmberText, fontWeight: T.fwSemibold }}
          >
            {needsEyes.length + unrecognized.length === 1
              ? "1 column needs your attention"
              : `${needsEyes.length + unrecognized.length} columns need your attention`}
          </span>
        )}
      </p>

      {/* ── Saved profiles list ────────────────────────────────────────────
           INGEST-PIPELINE-TYPE-AWARE-S1: name, type, mappings, last-used,
           edit/delete — wired to the same save flow used below. */}
      <div style={{ marginBottom: 12 }}>
        <button
          type="button"
          onClick={() => setShowProfilesList((v) => !v)}
          data-testid="toggle-profiles-list"
          style={{
            ...S.btnBase,
            ...S.btnSecondary,
            fontSize: T.fsXs5,
            padding: "5px 12px",
          }}
        >
          {showProfilesList ? "Hide saved profiles" : "Manage saved profiles"}
        </button>
        {showProfilesList && (
          <div
            data-testid="profiles-list"
            style={{
              marginTop: 8,
              border: `1px solid ${T.border}`,
              borderRadius: 8,
              overflow: "hidden",
            }}
          >
            {profilesLoading && (
              <div style={{ padding: 14, fontSize: T.fsSm5, color: T.mid }}>Loading profiles…</div>
            )}
            {profilesError && (
              <div role="alert" style={{ padding: 14, fontSize: T.fsSm5, color: T.red }}>
                {profilesError}
              </div>
            )}
            {!profilesLoading && !profilesError && profiles.length === 0 && (
              <div style={{ padding: 14, fontSize: T.fsSm5, color: T.mid }}>
                No saved profiles yet.
              </div>
            )}
            {!profilesLoading && profiles.map((p) => {
              const mappingCount = Object.keys(p.column_map ?? {}).length;
              const typeLabel = DATA_TYPE_LABELS[p.dataType] ?? p.dataType ?? p.source_type;
              return (
                <div
                  key={p.id}
                  data-testid={`profile-row-${p.id}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "9px 14px",
                    borderTop: `1px solid ${T.border}`,
                    fontSize: T.fsSm5,
                  }}
                >
                  <span style={{ flex: "1 1 160px", fontWeight: T.fwSemibold, color: T.dark }}>
                    {p.name}
                  </span>
                  <span style={{ flex: "0 0 140px", color: T.mid }}>{typeLabel}</span>
                  <span style={{ flex: "0 0 100px", color: T.mid }}>
                    {mappingCount} mapping{mappingCount === 1 ? "" : "s"}
                  </span>
                  <span style={{ flex: "0 0 130px", color: T.light, fontSize: T.fsXs5 }}>
                    {p.last_used_at
                      ? `Last used ${new Date(p.last_used_at).toLocaleDateString()}`
                      : "Never used"}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleEditProfile(p)}
                    data-testid={`edit-profile-btn-${p.id}`}
                    style={{
                      ...S.btnBase, ...S.btnSecondary, fontSize: T.fsXs5, padding: "4px 10px",
                    }}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteProfile(p.id)}
                    disabled={deletingProfileId === p.id}
                    data-testid={`delete-profile-btn-${p.id}`}
                    style={{
                      ...S.btnBase, ...S.btnSecondary, fontSize: T.fsXs5, padding: "4px 10px",
                      color: T.red, borderColor: T.red,
                      opacity: deletingProfileId === p.id ? 0.6 : 1,
                    }}
                  >
                    {deletingProfileId === p.id ? "Deleting…" : "Delete"}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Detected profile banner ───────────────────────────────────────
           Green when sourceProfile is set (coverage ≥ 60%); yellow when a
           profile name was suggested but coverage was too low to auto-classify. */}
      {stageResult?.suggestedProfileName && (() => {
        const highCoverage = !!stageResult.sourceProfile;
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: highCoverage ? 6 : 14,
              fontSize: T.fsSm5,
              ...(highCoverage ? {} : {
                padding: "9px 14px",
                borderRadius: 7,
                background: T.warnAmberBg,
                border: `1px solid ${T.utilAmberBorder}`,
              }),
            }}
            data-testid="detected-profile-banner"
          >
            <span
              style={{
                fontWeight: T.fwSemibold,
                color: highCoverage ? T.statusDoneText : T.warnAmberText,
              }}
            >
              {highCoverage
              ? `✓ Recognized this file as your '${stageResult.suggestedProfileName}' - we've pre-filled the column mappings below. Review and continue.`
              : `⚠ Detected profile: ${stageResult.suggestedProfileName}`}
            </span>
            {!highCoverage && (
              <span style={{ color: T.warnAmberText, fontSize: T.fsXs5 }}>
 - Manual column mapping may be needed
              </span>
            )}
          </div>
        );
      })()}

      {/* ── Profile match banner (state b) ───────────────────────────────
           Shown directly after the detected-profile notice so both ✓ lines
           are clustered above the COLUMNS heading. */}
      {hasProfile && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 6,
            fontSize: T.fsSm5,
          }}
          data-testid="profile-match-banner"
        >
          <span style={{ color: T.statusDoneText, fontWeight: T.fwSemibold }}>
            ✓{" "}
            {autoMappedCount === totalCount
              ? `All ${autoMappedCount} columns mapped automatically via "${stageResult.sourceProfile.name}"`
              : `${autoMappedCount} of ${totalCount} columns mapped automatically via "${stageResult.sourceProfile.name}"`}
          </span>
        </div>
      )}

      {/* ── Legacy column warnings ─────────────────────────────────────────
           Shown when the uploaded file contains old column names that the
           adapter does not recognise — prevents silent data loss.
           Blocks the "Next" footer button unless an admin overrides. */}
      {stageResult?.profileWarnings?.length > 0 && (
        <div
          style={{
            padding: "10px 14px",
            borderRadius: 7,
            background: T.warnAmberBg,
            border: `1px solid ${T.utilAmberBorder}`,
            marginBottom: 14,
            fontSize: T.fsSm5,
          }}
          data-testid="profile-warnings-banner"
        >
          <div
            style={{
              fontWeight: T.fwSemibold,
              color: T.warnAmberText,
              marginBottom: 6,
            }}
          >
            ⚠ Legacy column names detected - these columns will not be imported
          </div>
          {stageResult.profileWarnings.length > 1 ? (
            <ul style={{ margin: "0 0 8px", paddingLeft: 18, color: T.warnAmberText }}>
              {stageResult.profileWarnings.map((w, i) => (
                <li key={i} style={{ marginBottom: 2, fontSize: T.fsXs5 }}>{w}</li>
              ))}
            </ul>
          ) : (
            <p style={{ margin: "0 0 8px", color: T.warnAmberText, fontSize: T.fsXs5 }}>
              {stageResult.profileWarnings[0]}
            </p>
          )}
          <p
            style={{ margin: 0, color: T.warnAmberText, fontSize: T.fsXs5 }}
            data-testid="profile-warnings-action"
          >
            To fix this: re-export the file from Spacebase using a current template, or rename
            the affected columns to match the current format before uploading.
          </p>
          {isAdmin && (
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginTop: 10,
                fontSize: T.fsXs5,
                color: T.warnAmberText,
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={legacyOverride}
                onChange={(e) => onLegacyOverrideChange(e.target.checked)}
                data-testid="legacy-override-checkbox"
              />
              I know what I&apos;m doing - proceed anyway
            </label>
          )}
        </div>
      )}

      {/* ── COLUMNS group label — above first section so bars read as
           content categories, not workflow steps ────────────────────────── */}
      <div
        style={{
          fontSize: T.fsXs,
          fontWeight: T.fwSemibold,
          color: T.light,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          marginBottom: 6,
        }}
        data-testid="map-columns-label"
      >
        Columns
      </div>

      {/* ── Section 2.1: Mapped to WorkplaceOS (unified) ─────────────────
           All auto-confirmed and standard mapped columns appear here.
           If genuinely unconfirmed needsEyes items remain (low/medium
           confidence), a "Confirm match key" sub-list is shown first. */}
      <MapSection
        title={`2.1  Mapped to ${APP_NAME}`}
        description={`${APP_NAME} recognized these columns automatically. Change any mapping if needed.`}
        count={mapped.length}
        accentBorder={T.statusDoneBorder}
        open={open["mapped"]}
        onToggle={() => toggleOpen("mapped")}
        testId="section-mapped"
      >
        {needsEyes.length > 0 && (
          <div data-testid="confirm-match-key-sublist">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 14px",
                borderBottom: `1px solid ${T.border}`,
                background: T.pageBg,
              }}
            >
              <span style={{ fontSize: T.fsXs5, fontWeight: T.fwSemibold, color: T.warnAmberText }}>
                Confirm match key ({needsEyes.length})
              </span>
              <button
                type="button"
                data-testid="confirm-all-btn"
                onClick={handleConfirmAll}
                style={{
                  padding: "4px 14px",
                  borderRadius: 5,
                  fontSize: T.fsXs5,
                  fontWeight: T.fwSemibold,
                  background: T.statusDoneBg,
                  color: T.statusDoneText,
                  border: `1px solid ${T.statusDoneBorder}`,
                  cursor: "pointer",
                }}
              >
                Confirm all
              </button>
            </div>
            {needsEyes.map((item) => (
              <NeedsEyesRow key={item.csvColumn} item={item} />
            ))}
          </div>
        )}
        {mapped.length === 0 && needsEyes.length === 0 ? (
          <p style={{ padding: "10px 14px", color: T.mid, fontSize: T.fsXs5, margin: 0 }}>
            No columns mapped yet.
          </p>
        ) : (
          mapped.map((item) => (
            <MappedRow
              key={item.csvColumn}
              item={item}
              onChangeField={handleChangeField}
              mappedCanonicals={mappedCanonicals}
              dataTypeKey={dataTypeKey}
              badge={badgeForCanonical(item.canonicalField)}
            />
          ))
        )}
      </MapSection>

      {/* ── Section 2.2: Recognized — skipped ────────────────────────────
           All "not-used" columns: recognized and intentionally skipped
           (identity fields, low-value columns, currency duplicates, untracked
           fields, etc.).  Always rendered so the bucket is discoverable even
           when empty. */}
      <MapSection
        title="2.2  Recognized - skipped"
        description="These columns are recognized but not imported. Use 'Map anyway' if a column should be included."
        count={skippedDuplicate.length}
        accentBorder={T.border}
        open={open["skipped-duplicate"] ?? false}
        onToggle={() => toggleOpen("skipped-duplicate")}
        testId="section-skipped-duplicate"
      >
        {skippedDuplicate.length === 0 ? (
          <p style={{ padding: "10px 14px", color: T.mid, fontSize: T.fsXs5, margin: 0 }}>
            No duplicate-currency columns detected.
          </p>
        ) : (
          skippedDuplicate.map((item) => (
            <SkippedDuplicateRow
              key={item.csvColumn}
              item={item}
              onMapAnyway={handleMapAnyway}
            />
          ))
        )}
      </MapSection>

      {/* ── Section 2.3: Not used ─────────────────────────────────────────── */}
      <MapSection
        title={`2.3  Not used by ${APP_NAME} - no action needed`}
        description="These columns are skipped on import. Use 'Map anyway' if a column should be included."
        count={notUsed.length}
        accentBorder={T.border}
        open={open["not-used"]}
        onToggle={() => toggleOpen("not-used")}
        testId="section-not-used"
      >
        {notUsed.length === 0 ? (
          <p style={{ padding: "10px 14px", color: T.mid, fontSize: T.fsXs5, margin: 0 }}>
            No columns in this category.
          </p>
        ) : (
          notUsed.map((item) => (
            <NotUsedRow
              key={item.csvColumn}
              item={item}
              onMapAnyway={handleMapAnyway}
            />
          ))
        )}
      </MapSection>

      {/* ── Section 2.4: Unrecognized (always rendered) ──────────────────── */}
      <MapSection
        title="2.4  Unrecognized"
        description="We don't know what these columns contain. Ignore them or map them to a known field."
        count={unrecognized.length}
        accentBorder={unrecognized.length > 0 ? T.utilAmberBorder : T.border}
        badgeVariant={unrecognized.length > 0 ? "warning" : undefined}
        open={open["unrecognized"]}
        onToggle={() => toggleOpen("unrecognized")}
        testId="section-unrecognized"
      >
        {unrecognized.length === 0 ? (
          <p style={{ padding: "10px 14px", color: T.mid, fontSize: T.fsXs5, margin: 0 }}>
            No unrecognized columns.
          </p>
        ) : (
          unrecognized.map((item) => (
            <UnrecognizedRow
              key={item.csvColumn}
              item={item}
              onIgnore={handleIgnore}
              onMap={handleMap}
              mappedCanonicals={mappedCanonicals}
              dataTypeKey={dataTypeKey}
            />
          ))
        )}
      </MapSection>

      {/* ── Save profile controls ─────────────────────────────────────────── */}
      {hasProfile ? (
        /* State (b): profile matched — single always-visible Save button, gated on isDirty */
        <div style={{ marginTop: 8 }}>
          {saved ? (
            <p style={{ fontSize: T.fsSm5, color: T.statusDoneText, margin: "0 0 4px" }}>
              ✓ Profile updated.
            </p>
          ) : null}
          <button
            type="button"
            disabled={!isDirty || saving || needsEyes.length > 0}
            onClick={handleSaveProfile}
            style={{
              ...S.btnBase,
              ...S.btnSecondary,
              fontSize: T.fsSm5,
              color: isDirty ? T.blueDeep : T.mid,
              border: `1px solid ${isDirty ? T.blueDeep : T.border}`,
              padding: "6px 16px",
              opacity: (saving || needsEyes.length > 0) ? 0.6 : 1,
            }}
            title={needsEyes.length > 0 ? "Resolve all needs-eyes columns before saving the profile" : undefined}
            data-testid="save-profile-btn"
          >
            {saving ? "Saving…" : "Save changes to profile"}
          </button>
          {needsEyes.length > 0 ? (
            <p style={{ margin: "4px 0 0", fontSize: T.fsXs5, color: T.mid }}>
              Resolve {needsEyes.length} needs-eyes column{needsEyes.length === 1 ? "" : "s"} before saving.
            </p>
          ) : !isDirty && !saved && (
            <p style={{ margin: "4px 0 0", fontSize: T.fsXs5, color: T.mid }}>
              Profile up to date - no changes to save.
            </p>
          )}
        </div>
      ) : (
        /* State (a): first-time — existing toggle → name input → Save flow */
        <>
          {saved && (
            <p style={{ fontSize: T.fsSm5, color: T.statusDoneText, margin: "8px 0 0" }}>
              ✓ Profile saved - future imports of this report will auto-map.
            </p>
          )}
          {!saved && !showSave && (
            <div style={{ marginTop: 8 }}>
              <button
                type="button"
                style={{
                  ...S.btnBase,
                  ...S.btnSecondary,
                  fontSize: T.fsSm5,
                  color: T.blueDeep,
                  border: `1px solid ${T.blueDeep}`,
                  padding: "6px 16px",
                }}
                onClick={() => setShowSave(true)}
                data-testid="save-profile-toggle"
              >
                Save as Profile
              </button>
              <p style={{ margin: "4px 0 0", fontSize: T.fsXs5, color: T.light }}>
                A profile remembers these column mappings so the next upload of this report skips manual mapping.
              </p>
            </div>
          )}
          {showSave && (
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8 }}>
              <input
                type="text"
                value={profileName}
                onChange={(e) => setProfileName(e.target.value)}
                placeholder="Profile name"
                data-testid="profile-name-input"
                style={{
                  padding: "5px 10px",
                  borderRadius: 5,
                  border: `1px solid ${T.border}`,
                  fontSize: T.fsSm5,
                  color: T.dark,
                  minWidth: 200,
                }}
              />
              <button
                type="button"
                disabled={saving || !profileName.trim() || needsEyes.length > 0}
                onClick={handleSaveProfile}
                style={{
                  ...S.btnBase,
                  ...S.btnPrimary,
                  fontSize: T.fsXs5,
                  opacity: (saving || needsEyes.length > 0) ? 0.6 : 1,
                }}
                title={needsEyes.length > 0 ? "Resolve all needs-eyes columns before saving the profile" : undefined}
                data-testid="save-profile-btn"
              >
                {saving ? "Saving…" : "Save"}
              </button>
              <button
                type="button"
                onClick={() => setShowSave(false)}
                style={{ ...S.btnBase, ...S.btnSecondary, fontSize: T.fsXs5 }}
              >
                Cancel
              </button>
            </div>
          )}
        </>
      )}
      {saveError && (
        <p role="alert" style={{ fontSize: T.fsXs5, color: T.red, marginTop: 6 }}>
          {saveError}
        </p>
      )}
    </div>
  );
}

// ─── Step 3: Validate ─────────────────────────────────────────────────────────

// Extract all unique 3-letter currency codes across all warning message strings.
function extractCurrencyCodes(items) {
  const codes = new Set();
  for (const item of items) {
    const quoted = item.message.match(/['"]([A-Z]{3})['"]/);
    if (quoted) { codes.add(quoted[1]); continue; }
    const bare = item.message.match(/\b([A-Z]{3})\b/);
    if (bare) codes.add(bare[1]);
  }
  return codes.size > 0 ? [...codes] : ["unknown"];
}

// Return plain-language group metadata for a given field + items.
function getFieldOutcome(field, items) {
  const hasError = items.some((i) => i.severity === "error");
  const n = items.length;
  const rows = `${n} row${n !== 1 ? "s" : ""}`;

  if (hasError) {
    return {
      groupLabel: field,
      outcomeChip: "row blocked",
      description: null,
    };
  }

  // INGEST-VALIDATE-ADVISORY-VS-UNUSABLE-SEVERITY: statistical-heuristic
  // checks (currently peak > 3× average) are tagged tier: "advisory" by the
  // server. These values are never blanked or excluded — the real value
  // always imports as-is — so they must never fall into the generic
  // "Value can't be used" bucket below, which implies data loss.
  const isAdvisory = items.some((i) => i.tier === "advisory");
  if (isAdvisory) {
    return {
      groupLabel: "Worth a look - imports as-is",
      outcomeChip: "advisory",
      description:
        `${rows} flagged by a statistical heuristic (e.g. an unusually high peak vs. average). ` +
        `Nothing is blanked or excluded - the real value imports unchanged. Review and acknowledge if expected (e.g. an all-hands day).`,
    };
  }

  if (field === "currency") {
    const codes = extractCurrencyCodes(items);
    const codeList = codes.map((c) => `'${c}'`).join(", ");
    const multi = codes.length > 1;
    const currencyWord = multi ? "Currencies" : "Currency";
    const haveWord = multi ? "have" : "has";
    const addPromptPrefix = multi
      ? `Add ${codeList} rates (`
      : `Add a ${codes[0]} rate (`;
    return {
      groupLabel: "Currency has no exchange rate",
      outcomeChip: "kept as-is",
      description: (
        <>
          {`${currencyWord} ${codeList} ${haveWord} no exchange rate on file. These ${rows} keep their native values - nothing is converted automatically. ${addPromptPrefix}`}
          <a href="/admin/currency-rates">Admin → Currency Rates</a>
          {`) to see USD, or these may be out-of-scope locations you'll skip in Review.`}
        </>
      ),
    };
  }

  if (field === "size" || field.startsWith("size_")) {
    return {
      groupLabel: "Size is unusable",
      outcomeChip: "field excluded",
      description:
        `${n} row${n !== 1 ? "s" : ""} have a Size of 0 or blank. That Size value won't be imported; the rest of each row imports normally. Fix at source or enter manually after import.`,
    };
  }

  return {
    groupLabel: "Value can't be used",
    outcomeChip: "field excluded",
    description:
      `These ${rows} have a value we can't use. The affected field will be left blank; the rest of each row imports normally.`,
  };
}

export function ValidateStep({ sessionId, onBack, onDone, onValidate }) {
  const [result, setResult]         = useState(null);
  const [busy, setBusy]             = useState(false);
  const [error, setError]           = useState(null);
  const [expanded, setExpanded]     = useState({});
  // Map from rowIndex (number) → lease/location identifier string
  const [rowIdMap, setRowIdMap]     = useState(new Map());
  // INGEST-PIPELINE-TYPE-AWARE-S1: rowIndexes acknowledged this session, so the
  // acknowledged anomaly disappears immediately without a full re-validate.
  const [acknowledgedRows, setAcknowledgedRows] = useState(new Set());
  const [acknowledging, setAcknowledging]       = useState(null); // rowIndex currently in flight

  // Run validation and staged-row fetch in parallel on mount
  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    Promise.all([
      validateStagedSession(sessionId),
      getStagedRows(sessionId).catch(() => ({ rows: [] })),
    ])
      .then(([r, stagedData]) => {
        if (!cancelled) {
          setResult(r);
          // Auto-open groups that contain errors
          const autoOpen = {};
          for (const item of r.items) {
            if (item.severity === "error") autoOpen[item.field] = true;
          }
          setExpanded(autoOpen);
          onValidate?.(r.status);
          // Build rowIndex → identifier map from staged rows
          const idMap = new Map();
          for (const row of (stagedData?.rows ?? [])) {
            const identifier =
              row.normalized_data?.lease_id ??
              row.normalized_data?.location_id ??
              null;
            if (identifier != null) {
              idMap.set(row.row_index, identifier);
            }
          }
          setRowIdMap(idMap);
        }
      })
      .catch((err) => { if (!cancelled) setError(err.message || "Validation failed"); })
      .finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
  }, [sessionId, onValidate]);

  const toggleExpand = (field) =>
    setExpanded((prev) => ({ ...prev, [field]: !prev[field] }));

  const statusMap = {
    passed:   { bg: T.statusDoneBg, color: T.statusDoneText, border: T.statusDoneBorder },
    warnings: { bg: T.warnAmberBg,  color: T.warnAmberText,  border: T.utilAmberBorder },
    errors:   { bg: T.utilRed,      color: T.red,            border: T.redRing },
  };

  if (busy) {
    return (
      <div style={{ padding: "32px 0", textAlign: "center", color: T.mid, fontSize: T.fsSm5 }}>
        Running validation…
      </div>
    );
  }

  if (error) {
    return (
      <div
        role="alert"
        style={{ padding: "12px 16px", background: T.utilRed, color: T.red, borderRadius: 6, fontSize: T.fsSm5 }}
      >
        {error}
      </div>
    );
  }

  if (!result) return null;

  const { status, rowCount, items: rawItems } = result;
  // INGEST-PIPELINE-TYPE-AWARE-S1: drop acknowledged anomaly warnings from the
  // active list — acknowledging never re-nags on subsequent passes.
  const items = rawItems.filter(
    (i) => !(i.acknowledgeable && acknowledgedRows.has(i.rowIndex)),
  );
  const { bg, color, border } = statusMap[status] ?? statusMap.passed;
  const warnCount = Object.values(
    items.reduce((acc, i) => { acc[i.field] = true; return acc; }, {})
  ).length;
  // Count distinct rows that have at least one error (not total rows checked)
  const blockedRowCount = new Set(
    items.filter((i) => i.severity === "error").map((i) => i.rowIndex)
  ).size;

  // Build banner label from status
  const bannerLabel = status === "passed"
    ? `${rowCount} row${rowCount !== 1 ? "s" : ""} checked - all safe to review.`
    : status === "warnings"
      ? `${rowCount} row${rowCount !== 1 ? "s" : ""} checked - ${warnCount} thing${warnCount !== 1 ? "s" : ""} to note.`
      : `${blockedRowCount} row${blockedRowCount !== 1 ? "s" : ""} can't be imported - fix these before continuing.`;

  // Group items by field
  const byField = {};
  for (const item of items) {
    (byField[item.field] ??= []).push(item);
  }

  return (
    <div data-testid="validation-result">
      {/* Lead copy — clarifies advisory nature before the user sees any warnings */}
      <p
        style={{ fontSize: T.fsSm5, color: T.mid, marginTop: 0, marginBottom: 12 }}
        data-testid="validate-lead-copy"
      >
        These are advisories - nothing is broken and nothing imports automatically here.
        Review, optionally fix, then continue.
      </p>

      {/* Status banner */}
      {status === "errors" ? (
        <div
          style={{
            padding: "10px 16px",
            borderRadius: 7,
            background: bg,
            border: `1px solid ${border}`,
            color,
            fontSize: T.fsSm5,
            fontWeight: T.fwSemibold,
            marginBottom: 16,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
          data-testid="validation-status-banner"
        >
          <span>✗</span>
          <span>{bannerLabel}</span>
        </div>
      ) : (
        <p
          style={{
            color: T.statusDoneText,
            fontWeight: T.fwSemibold,
            fontSize: T.fsSm5,
            marginTop: 0,
            marginBottom: 16,
          }}
          data-testid="validation-status-banner"
        >
          ✓ {bannerLabel}
        </p>
      )}

      {/* Issue list */}
      {items.length === 0 && (
        <p style={{ color: T.mid, fontSize: T.fsSm5 }}>
          No issues found - all rows passed validation.
        </p>
      )}
      {Object.entries(byField).map(([field, fieldItems], index) => {
        const isOpen = expanded[field] ?? false;
        const hasError = fieldItems.some((i) => i.severity === "error");
        const { groupLabel, description } = getFieldOutcome(field, fieldItems);
        const prefixedLabel = `3.${index + 1}  ${groupLabel}`;
        return (
          <div
            key={field}
            style={{
              border: `1px solid ${T.border}`,
              borderLeft: hasError ? `1px solid ${T.border}` : `3px solid ${T.utilAmberBorder}`,
              borderRadius: 7,
              marginBottom: 8,
              overflow: "hidden",
            }}
            data-testid={`validation-group-${field}`}
          >
            <button
              type="button"
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                padding: "9px 14px",
                background: T.pageBg,
                border: "none",
                cursor: "pointer",
                textAlign: "left",
                gap: 8,
                fontSize: T.fsSm5,
              }}
              onClick={() => toggleExpand(field)}
            >
              <span style={{ fontWeight: T.fwSemibold, color: T.dark, flex: 1 }}>
                {prefixedLabel}
              </span>
              <span style={{ color: T.mid, fontSize: T.fsXs }}>
                {fieldItems.length}
              </span>
              <span style={{ color: T.mid, fontSize: T.fsXs }}>{isOpen ? "▲" : "▼"}</span>
            </button>
            {isOpen && (
              <div style={{ background: T.card }}>
                {description && (
                  <p
                    style={{
                      margin: 0,
                      padding: "10px 14px 6px",
                      fontSize: T.fsXs5,
                      color: T.mid,
                    }}
                    data-testid={`group-description-${field}`}
                  >
                    {description}
                  </p>
                )}
                <ul
                  style={{
                    margin: 0,
                    padding: "0 14px 10px",
                    listStyle: "none",
                  }}
                >
                  {fieldItems.map((item, i) => {
                    const identifier = rowIdMap.get(item.rowIndex);
                    const rowLabel = identifier ?? `Row ${item.rowIndex + 1}`;
                    return (
                      <li
                        key={i}
                        style={{
                          padding: "5px 0",
                          borderTop: i > 0 ? `1px solid ${T.border}` : "none",
                          fontSize: T.fsXs5,
                          color: item.severity === "error" ? T.red : T.warnAmberText,
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                        data-testid={`validation-item-${field}-${item.rowIndex}`}
                      >
                        <span style={{ minWidth: 40, color: T.mid, fontFamily: identifier ? "monospace" : undefined }}>{rowLabel}</span>
                        <span style={{ flex: 1 }}>{item.message}</span>
                        {item.acknowledgeable && (
                          <button
                            type="button"
                            data-testid={`acknowledge-anomaly-btn-${item.rowIndex}`}
                            disabled={acknowledging === item.rowIndex}
                            onClick={async () => {
                              setAcknowledging(item.rowIndex);
                              try {
                                await acknowledgeAnomaly(sessionId, item.rowIndex);
                                setAcknowledgedRows((prev) => new Set(prev).add(item.rowIndex));
                              } catch {
                                // non-fatal — user can retry the acknowledge action
                              } finally {
                                setAcknowledging(null);
                              }
                            }}
                            style={{
                              flexShrink: 0,
                              padding: "2px 9px",
                              borderRadius: 4,
                              border: `1px solid ${T.utilAmberBorder}`,
                              background: "transparent",
                              color: T.warnAmberText,
                              fontSize: T.fsXs,
                              fontWeight: T.fwSemibold,
                              cursor: "pointer",
                              opacity: acknowledging === item.rowIndex ? 0.6 : 1,
                            }}
                          >
                            {acknowledging === item.rowIndex ? "Acknowledging…" : "Acknowledge"}
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        );
      })}

    </div>
  );
}

// ─── Diff card (exported; used by ImportSessionReview and DiffRow helpers) ───

// Small badge used to show materiality tier on each field row.
function TierBadge({ tier, isConflict }) {
  if (isConflict) {
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          padding: "1px 7px",
          borderRadius: 10,
          fontSize: T.fsXs,
          fontWeight: T.fwSemibold,
          background: T.purpleBg,
          color: T.purple,
        }}
      >
        Conflict
      </span>
    );
  }
  if (tier === "material") {
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          padding: "1px 7px",
          borderRadius: 10,
          fontSize: T.fsXs,
          fontWeight: T.fwSemibold,
          background: T.utilRed,
          color: T.red,
        }}
      >
        Material
      </span>
    );
  }
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "1px 7px",
        borderRadius: 10,
        fontSize: T.fsXs,
        fontWeight: T.fwSemibold,
        background: T.pageBg,
        color: T.mid,
        border: `1px solid ${T.border}`,
      }}
    >
      Low
    </span>
  );
}

// Per-record diff card — shows all changed fields with Accept/Reject controls.
export function DiffCard({ record, decisions, onDecide, onBulkDecide }) {
  const { leaseId, incomingId, areas, sourcePrecedence, fields } = record;
  const [lowExpanded, setLowExpanded] = useState(false);

  // spot_check_warning is a meta-advisory injected by the custom-report adapter.
  // It is shown as a header badge and excluded from the field diff tables.
  const spotCheckWarning = fields.find((f) => f.fieldName === "spot_check_warning");
  const regularFields    = fields.filter((f) => f.fieldName !== "spot_check_warning");

  const materialFields = regularFields.filter((f) => f.tier === "material" || f.isConflict);
  const lowFields      = regularFields.filter((f) => f.tier !== "material" && !f.isConflict);

  const decisionKey  = (f) => `${leaseId ?? incomingId}.${f.fieldName}`;
  const getDecision  = (f) => decisions[decisionKey(f)] ?? f.defaultDecision;

  const renderValueCell = (val) => {
    if (val == null) return <em style={{ color: T.mid }}>—</em>;
    return String(val);
  };

  const fieldRow = (f, idx, arr) => {
    const key      = decisionKey(f);
    const decision = decisions[key] ?? f.defaultDecision;
    const isConflict = f.isConflict;

    const rowBg = isConflict ? T.purpleBg : "transparent";

    return (
      <tr
        key={f.fieldName}
        style={{
          borderBottom: idx < arr.length - 1 ? `1px solid ${T.border}` : "none",
          background: rowBg,
        }}
      >
        <td style={{ padding: "6px 12px", color: T.dark, fontFamily: "monospace", fontSize: T.fsXs5, width: 180 }}>
          {f.fieldName}
        </td>
        <td style={{ padding: "6px 12px", color: T.mid, fontSize: T.fsSm5 }}>
          {renderValueCell(f.current)}
        </td>
        <td style={{ padding: "6px 12px", color: T.dark, fontSize: T.fsSm5, fontWeight: T.fwSemibold }}>
          {renderValueCell(f.incoming)}
          {f.magnitude && (
            <span style={{ marginLeft: 6, fontSize: T.fsXs, color: T.mid, fontWeight: T.fwNormal }}>
              ({f.magnitude})
            </span>
          )}
        </td>
        <td style={{ padding: "6px 12px", width: 90 }}>
          <TierBadge tier={f.tier} isConflict={isConflict} />
        </td>
        <td style={{ padding: "6px 10px", width: 200 }}>
          {isConflict ? (
            // Conflict row: Keep mine / Take new toggle
            <div style={{ display: "flex", gap: 4 }}>
              <button
                type="button"
                onClick={() => onDecide(key, "keepMine")}
                style={{
                  padding: "2px 8px",
                  borderRadius: 4,
                  fontSize: T.fsXs,
                  fontWeight: T.fwSemibold,
                  border: `1px solid ${decision === "keepMine" ? T.purple : T.border}`,
                  background: decision === "keepMine" ? T.purpleBg : T.card,
                  color: decision === "keepMine" ? T.purple : T.mid,
                  cursor: "pointer",
                }}
              >
                Keep mine
              </button>
              <button
                type="button"
                onClick={() => onDecide(key, "takeNew")}
                style={{
                  padding: "2px 8px",
                  borderRadius: 4,
                  fontSize: T.fsXs,
                  fontWeight: T.fwSemibold,
                  border: `1px solid ${decision === "takeNew" ? T.purple : T.border}`,
                  background: decision === "takeNew" ? T.purpleBg : T.card,
                  color: decision === "takeNew" ? T.purple : T.mid,
                  cursor: "pointer",
                }}
              >
                Take new
              </button>
            </div>
          ) : (
            // Regular diff: Accept / Reject
            <div style={{ display: "flex", gap: 4 }}>
              <button
                type="button"
                onClick={() => onDecide(key, "accept")}
                style={{
                  padding: "2px 8px",
                  borderRadius: 4,
                  fontSize: T.fsXs,
                  fontWeight: T.fwSemibold,
                  border: `1px solid ${decision === "accept" ? T.statusDoneBorder : T.border}`,
                  background: decision === "accept" ? T.statusDoneBg : T.card,
                  color: decision === "accept" ? T.statusDoneText : T.mid,
                  cursor: "pointer",
                }}
              >
                Accept
              </button>
              <button
                type="button"
                onClick={() => onDecide(key, "reject")}
                style={{
                  padding: "2px 8px",
                  borderRadius: 4,
                  fontSize: T.fsXs,
                  fontWeight: T.fwSemibold,
                  border: `1px solid ${decision === "reject" ? T.redRing : T.border}`,
                  background: decision === "reject" ? T.utilRed : T.card,
                  color: decision === "reject" ? T.red : T.mid,
                  cursor: "pointer",
                }}
              >
                Reject
              </button>
            </div>
          )}
        </td>
        {sourcePrecedence[f.fieldName] && (
          <td style={{ padding: "6px 10px", fontSize: T.fsXs, color: T.mid, fontStyle: "italic" }}>
            Source: {sourcePrecedence[f.fieldName].replace(/_/g, " ")}
          </td>
        )}
      </tr>
    );
  };

  return (
    <div
      style={{
        border: `1px solid ${T.border}`,
        borderRadius: 8,
        overflow: "hidden",
        marginBottom: 12,
      }}
      data-testid="diff-record-card"
    >
      {/* Card header */}
      <div
        style={{
          padding: "10px 16px",
          background: T.pageBg,
          borderBottom: `1px solid ${T.border}`,
          display: "flex",
          alignItems: "center",
          gap: 12,
          fontSize: T.fsSm5,
          flexWrap: "wrap",
        }}
      >
        <span style={{ fontWeight: T.fwSemibold, color: T.dark, fontFamily: "monospace" }}>
          {leaseId ?? incomingId ?? "—"}
        </span>
        {areas.length > 0 && (
          <span style={{ color: T.mid }}>
            Impacted areas: {areas.join(", ")}
          </span>
        )}
        {spotCheckWarning && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              padding: "2px 8px",
              borderRadius: 10,
              fontSize: T.fsXs,
              fontWeight: T.fwSemibold,
              background: T.warnAmberBg,
              color: T.warnAmberText,
              border: `1px solid ${T.utilAmberBorder}`,
            }}
            data-testid={`spot-check-warning-${leaseId ?? incomingId}`}
          >
            ⚠ {String(spotCheckWarning.incoming)}
          </span>
        )}
      </div>

      {/* Material + conflict field rows */}
      {materialFields.length > 0 && (
        <CornerTable style={{ width: "100%", borderCollapse: "collapse", fontSize: T.fsSm5 }}>
          <thead>
            <tr style={{ background: T.pageBg, borderBottom: `1px solid ${T.border}` }}>
              <th style={{ padding: "6px 12px", textAlign: "left", color: T.mid, fontWeight: T.fwSemibold }}>Field</th>
              <th style={{ padding: "6px 12px", textAlign: "left", color: T.mid, fontWeight: T.fwSemibold }}>Current</th>
              <th style={{ padding: "6px 12px", textAlign: "left", color: T.mid, fontWeight: T.fwSemibold }}>Incoming</th>
              <th style={{ padding: "6px 12px", textAlign: "left", color: T.mid, fontWeight: T.fwSemibold }}>Type</th>
              <th style={{ padding: "6px 12px", textAlign: "left", color: T.mid, fontWeight: T.fwSemibold }}>Decision</th>
            </tr>
          </thead>
          <tbody>
            {materialFields.map((f, i) => fieldRow(f, i, materialFields))}
          </tbody>
        </CornerTable>
      )}

      {/* Low / minor changes — collapsed batch row */}
      {lowFields.length > 0 && (
        <div style={{ borderTop: materialFields.length > 0 ? `1px solid ${T.border}` : "none" }}>
          <button
            type="button"
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              padding: "8px 14px",
              background: T.card,
              border: "none",
              cursor: "pointer",
              textAlign: "left",
              gap: 8,
              fontSize: T.fsXs5,
              color: T.mid,
            }}
            onClick={() => setLowExpanded((v) => !v)}
          >
            <span>{lowExpanded ? "▲" : "▼"}</span>
            <span>{lowFields.length} minor change{lowFields.length !== 1 ? "s" : ""}</span>
            {!lowExpanded && (
              <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onBulkDecide(leaseId ?? incomingId, lowFields, "accept"); }}
                  style={{
                    padding: "2px 8px", borderRadius: 4, fontSize: T.fsXs, fontWeight: T.fwSemibold,
                    border: `1px solid ${T.statusDoneBorder}`, background: T.statusDoneBg,
                    color: T.statusDoneText, cursor: "pointer",
                  }}
                >
                  Accept all minor
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onBulkDecide(leaseId ?? incomingId, lowFields, "reject"); }}
                  style={{
                    padding: "2px 8px", borderRadius: 4, fontSize: T.fsXs, fontWeight: T.fwSemibold,
                    border: `1px solid ${T.border}`, background: T.pageBg,
                    color: T.mid, cursor: "pointer",
                  }}
                >
                  Reject all minor
                </button>
              </span>
            )}
          </button>
          {lowExpanded && (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: T.fsSm5 }}>
              <tbody>
                {lowFields.map((f, i) => fieldRow(f, i, lowFields))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}


function ViewerNotice() {
  return (
    <div
      style={{
        padding: "20px 24px",
        background: T.pageBg,
        border: `1px solid ${T.border}`,
        borderRadius: 8,
        color: T.mid,
        fontSize: T.fsSm5,
      }}
      data-testid="import-wizard-viewer-notice"
    >
      <strong style={{ color: T.dark }}>Read-only access</strong>
      <br />
      You need editor or admin permissions to import data. Contact your Ripple administrator
      to request access.
    </div>
  );
}

// ─── Step 5: Done / Summary ─────────────────────────────────────────────────
//
// INGEST-SUMMARY-SCREEN-S1: type-aware post-apply receipt. Mirrors the
// receiptCopy labels in server/lib/importers/typeRegistry.js — kept as a
// small local map here since the client bundle doesn't share server modules.
const DONE_STEP_COPY = {
  leases: {
    titleLabel:         "Import applied to Leases",
    addedLabel:         "leases created",
    updatedLabel:       "leases updated",
    rejectedLabel:      "rows rejected",
    skippedLabel:       "rows skipped",
    targetDatasetLabel: "Leases",
    viewLabel:          "Leases",
  },
  badging: {
    titleLabel:         "Import applied to Badging",
    addedLabel:         "records added",
    updatedLabel:       "records updated",
    replacedLabel:      "prior records replaced",
    rejectedLabel:      "rows rejected",
    skippedLabel:       "rows skipped",
    targetDatasetLabel: "Badging",
    viewLabel:          "Badging",
  },
  headcount: {
    titleLabel:         "Headcount applied",
    rejectedLabel:      "rows rejected",
    skippedLabel:       "rows skipped (remote/distributed)",
    targetDatasetLabel: "Offices",
    viewLabel:          "Offices",
  },
};

export function DoneStep({ applyResult, mappingSnapshot, onOpenImportsQueue, onViewDataset, onDone }) {
  const dataType = applyResult?.dataType ?? "leases";
  const copy     = DONE_STEP_COPY[dataType] ?? DONE_STEP_COPY.leases;
  const receipt  = applyResult?.receipt ?? {
    added: 0, updated: 0, replaced: 0, rejected: 0, skipped: 0,
    held: 0, fallback: 0, vanishedNames: [],
  };
  const affectedLocations = applyResult?.affectedLocations ?? [];
  const period = applyResult?.period;

  const [profileName, setProfileName]   = useState("");
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [saving, setSaving]             = useState(false);
  const [saveError, setSaveError]       = useState(null);
  const [saved, setSaved]               = useState(false);

  const offerSaveProfile =
    !!mappingSnapshot &&
    !mappingSnapshot.hasProfile &&
    !mappingSnapshot.saved &&
    !saved;

  const handleSaveProfile = useCallback(async () => {
    if (!profileName.trim() || !mappingSnapshot) return;
    setSaving(true);
    setSaveError(null);
    try {
      const { column_map, dispositions } = buildProfilePayloadFromItems(mappingSnapshot.items);
      await saveSourceProfile({
        name:        profileName.trim(),
        source_type: mappingSnapshot.sourceType,
        column_map,
        dispositions,
      });
      setSaved(true);
      setShowSaveForm(false);
    } catch (err) {
      setSaveError(err.message || "Could not save profile");
    } finally {
      setSaving(false);
    }
  }, [profileName, mappingSnapshot]);

  const isHeadcount = dataType === "headcount";

  const headcountStatLine = isHeadcount
    ? `Headcount applied to ${receipt.added + receipt.updated} offices · as of ${applyResult?.asOfDate ?? "—"}`
      + (receipt.updated > 0 ? ` · ${receipt.updated} overwritten` : "")
    : null;

  const receiptRows = isHeadcount
    ? [
        { label: copy.rejectedLabel, value: receipt.rejected },
        { label: copy.skippedLabel,  value: receipt.skipped },
        ...(receipt.held > 0 ? [{ label: "Rows held (unmapped Work Location)", value: receipt.held }] : []),
        ...(receipt.fallback > 0 ? [{ label: "Rows using legacy fallback", value: receipt.fallback }] : []),
      ].filter((r) => r.value > 0)
    : [
        { label: copy.addedLabel,    value: receipt.added },
        { label: copy.updatedLabel,  value: receipt.updated },
        ...(copy.replacedLabel ? [{ label: copy.replacedLabel, value: receipt.replaced ?? 0 }] : []),
        { label: copy.rejectedLabel, value: receipt.rejected },
        { label: copy.skippedLabel,  value: receipt.skipped },
      ].filter((r) => r.value > 0 || r.label === copy.addedLabel || r.label === copy.updatedLabel);

  return (
    <div data-testid="done-step" style={{ maxWidth: 640 }}>
      <div
        style={{
          padding: "20px 24px",
          background: T.statusDoneBg,
          border: `1px solid ${T.statusDoneText}`,
          borderRadius: 8,
          marginBottom: 16,
        }}
      >
        <h3 style={{ margin: "0 0 4px", fontSize: T.fsLg, color: T.dark }}>
          ✓ {copy.titleLabel}
        </h3>
        {period && (
          <p style={{ margin: "0 0 12px", fontSize: T.fsSm5, color: T.mid }}>
            Period: {period.start} – {period.end}
          </p>
        )}
        {isHeadcount ? (
          <p
            data-testid="done-headcount-stat"
            style={{ margin: "8px 0 0", fontSize: T.fsSm5, color: T.dark }}
          >
            {headcountStatLine}
          </p>
        ) : (
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginTop: 8 }}>
            {receiptRows.map((r) => (
              <div key={r.label} data-testid={`done-receipt-${r.label.replace(/\s+/g, "-")}`}>
                <div style={{ fontSize: T.fsXl ?? 22, fontWeight: T.fwBold, color: T.dark }}>
                  {r.value}
                </div>
                <div style={{ fontSize: T.fsXs5, color: T.mid }}>{r.label}</div>
              </div>
            ))}
          </div>
        )}
        {isHeadcount && receiptRows.map((r) => (
          <p
            key={r.label}
            data-testid={`done-receipt-${r.label.replace(/\s+/g, "-")}`}
            style={{ margin: "4px 0 0", fontSize: T.fsXs5, color: T.mid }}
          >
            {r.value} {r.label}
          </p>
        ))}
      </div>

      {affectedLocations.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <h4 style={{ margin: "0 0 6px", fontSize: T.fsSm5, color: T.dark }}>
            Affected locations ({affectedLocations.length})
          </h4>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: T.fsSm5, color: T.mid }}>
            {affectedLocations.slice(0, 10).map((loc) => (
              <li key={loc.id}>{loc.alias ?? loc.id}</li>
            ))}
            {affectedLocations.length > 10 && (
              <li>+{affectedLocations.length - 10} more</li>
            )}
          </ul>
        </div>
      )}

      {isHeadcount && (receipt.held > 0 || receipt.fallback > 0 || receipt.vanishedNames?.length > 0) && (
        <div
          data-testid="done-headcount-drift"
          style={{
            marginBottom: 16,
            padding: "10px 12px",
            border: `1px solid ${T.utilAmberBorder}`,
            borderRadius: 6,
            background: T.warnAmberBg,
            color: T.warnAmberText,
            fontSize: T.fsXs5,
          }}
        >
          {receipt.held > 0 && <div>{receipt.held} aggregated row{receipt.held === 1 ? "" : "s"} held and excluded from office totals.</div>}
          {receipt.fallback > 0 && <div>{receipt.fallback} aggregated row{receipt.fallback === 1 ? "" : "s"} used a legacy location fallback.</div>}
          {receipt.vanishedNames?.length > 0 && <div>{receipt.vanishedNames.length} confirmed Work Location name{receipt.vanishedNames.length === 1 ? "" : "s"} vanished from this export.</div>}
        </div>
      )}

      {offerSaveProfile && (
        <div
          style={{
            marginBottom: 16,
            padding: "12px 16px",
            border: `1px solid ${T.border}`,
            borderRadius: 8,
          }}
        >
          {!showSaveForm ? (
            <>
              <p style={{ margin: "0 0 8px", fontSize: T.fsSm5, color: T.mid }}>
                Save this column mapping as a profile so future uploads of this report auto-map.
              </p>
              <button
                type="button"
                style={{ ...S.btnBase, ...S.btnSecondary, fontSize: T.fsSm5 }}
                onClick={() => setShowSaveForm(true)}
                data-testid="done-save-profile-toggle"
              >
                Save as Profile
              </button>
            </>
          ) : (
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type="text"
                value={profileName}
                onChange={(e) => setProfileName(e.target.value)}
                placeholder="Profile name"
                data-testid="done-profile-name-input"
                style={{
                  padding: "5px 10px",
                  borderRadius: 5,
                  border: `1px solid ${T.border}`,
                  fontSize: T.fsSm5,
                  minWidth: 200,
                }}
              />
              <button
                type="button"
                disabled={saving || !profileName.trim()}
                onClick={handleSaveProfile}
                style={{ ...S.btnBase, ...S.btnPrimary, fontSize: T.fsXs5, opacity: saving ? 0.6 : 1 }}
                data-testid="done-save-profile-btn"
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          )}
          {saveError && (
            <p role="alert" style={{ fontSize: T.fsXs5, color: T.red, marginTop: 6 }}>
              {saveError}
            </p>
          )}
        </div>
      )}
      {saved && (
        <p style={{ fontSize: T.fsSm5, color: T.statusDoneText, margin: "0 0 16px" }}>
          ✓ Profile saved - future imports of this report will auto-map.
        </p>
      )}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
        {onOpenImportsQueue && (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <button
              type="button"
              style={{ ...S.btnBase, ...S.btnSecondary }}
              onClick={() => onOpenImportsQueue()}
              data-testid="done-reconcile-btn"
            >
              Reconcile unmatched rows →
            </button>
            {dataType === "headcount" && receipt.skipped > 0 && (
              <span style={{ fontSize: T.fsXs5, color: T.mid }}>
                {receipt.skipped} remote/distributed rows were excluded from office upserts
              </span>
            )}
          </div>
        )}
        {onViewDataset && (
          <button
            type="button"
            style={{ ...S.btnBase, ...S.btnPrimary }}
              onClick={() => onViewDataset(dataType)}
            data-testid="done-view-dataset-btn"
          >
            View in {copy.viewLabel} →
          </button>
        )}
        <button
          type="button"
          style={{ ...S.btnBase, ...S.btnSecondary }}
          onClick={onDone}
          data-testid="wizard-done-btn"
        >
          Import another file
        </button>
        <button
          type="button"
          style={{ ...S.btnBase, ...S.btnSecondary }}
          onClick={onDone}
          data-testid="done-back-to-data-btn"
        >
          Back to Data
        </button>
      </div>
    </div>
  );
}

// ─── AsOfDateCard ─────────────────────────────────────────────────────────────
// Sub-step card shown between Map and Validate for headcount sessions.
// The admin confirms (or corrects) the as-of date parsed from the filename.
// Persists the date via PATCH /api/import-sessions/:id/confirm-date.
// The wizard's "Validate →" footer button stays disabled until onConfirmed fires.

// HEADCOUNT-IMPORT-S1-S4 Slice 4: expanded patterns for date extraction.
// Handles:
//   "Active Employees 2026-06-29.csv"               (simple)
//   "Active Employees 2026-06-29 14_30 UTC.csv"     (with time + tz)
//   "Active Employees 2026-06-29 14_30 UTC - Sheet1.csv"  (+ sheet suffix)
// Returns "" (empty) when no YYYY-MM-DD pattern is found — no silent fallback.
function parseDateFromFilename(filename) {
  const m = String(filename || "").match(/(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : "";
}

function AsOfDateCard({ sessionId, sourceFilename, persistedDate, onConfirmed }) {
  const parsedDate = parseDateFromFilename(sourceFilename);
  const [date, setDate]           = useState(() => persistedDate || parsedDate);
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed]   = useState(false);
  const [error, setError]           = useState(null);

  async function handleConfirm() {
    if (!date) return;
    setConfirming(true);
    setError(null);
    try {
      await confirmAsOfDate(sessionId, date);
      setConfirmed(true);
      onConfirmed(date);
    } catch (err) {
      setError(err?.message ?? "Failed to save date");
    } finally {
      setConfirming(false);
    }
  }

  return (
    <div
      data-testid="headcount-date-confirm-card"
      style={{
        border: `1px solid ${T.border}`,
        borderRadius: 8,
        padding: "20px 24px",
        background: T.card,
        maxWidth: 480,
      }}
    >
      <p style={{ margin: "0 0 4px", fontSize: T.fsSm, fontWeight: T.fwSemibold, color: T.dark }}>
        Confirm headcount as-of date
      </p>
      {parsedDate ? (
        <p style={{ margin: "0 0 16px", fontSize: T.fsXs5, color: T.mid }}>
          Reading the as-of date as <strong>{parsedDate}</strong> - correct? Edit if needed, then confirm.
        </p>
      ) : (
        <p style={{ margin: "0 0 16px", fontSize: T.fsXs5, color: T.warnAmberText }}>
          No date found in the filename. Enter the as-of date the snapshot represents before applying.
        </p>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <input
          type="date"
          value={date}
          onChange={(e) => { setDate(e.target.value); setConfirmed(false); }}
          disabled={confirming}
          required
          data-testid="headcount-date-input"
          style={{
            padding: "5px 8px",
            borderRadius: 4,
            border: `1px solid ${date ? T.border : T.utilAmberBorder}`,
            fontSize: T.fsSm5,
            color: T.dark,
          }}
        />
        <button
          type="button"
          disabled={!date || confirming || confirmed}
          onClick={handleConfirm}
          data-testid="headcount-date-confirm-btn"
          style={{
            ...S.btnBase,
            ...S.btnPrimary,
            opacity: (!date || confirming || confirmed) ? 0.5 : 1,
          }}
        >
          {confirmed ? "✓ Confirmed" : confirming ? "Saving…" : "Confirm date"}
        </button>
      </div>
      {!date && (
        <p style={{ margin: "0 0 8px", fontSize: T.fsXs5, color: T.warnAmberText }}>
          A date is required before applying.
        </p>
      )}
      {error && (
        <p role="alert" style={{ margin: 0, fontSize: T.fsXs5, color: T.red }}>
          {error}
        </p>
      )}
    </div>
  );
}

// ─── Main wizard ──────────────────────────────────────────────────────────────

const WIZARD_STORAGE_KEY = "ripple_import_wizard_v1";

export function ImportWizard({ currentRole, onOpenImportsQueue, onViewDataset, resumeSessionId }) {
  const [step, setStep]               = useState(1);
  const [busy, setBusy]               = useState(false);
  const [stageResult, setStageResult] = useState(null);
  const [validationStatus, setValidationStatus] = useState(null);
  // PDF sessions skip the Map step (column mapping is resolved by the adapter).
  const [isPdfSession, setIsPdfSession] = useState(false);
  // Admin-only override that unlocks the Next button when legacy columns are present.
  const [legacyOverride, setLegacyOverride] = useState(false);
  // Live count of needs-eyes items (updated by MapStep via callback, drives status strip).
  const [needsEyesCount, setNeedsEyesCount] = useState(0);
  // INGEST-SUMMARY-SCREEN-S1: latest apply result (drives Step 5 receipt) and
  // the lifted mapping snapshot (drives Step 5's save-profile-at-summary offer).
  const [applyResult, setApplyResult]         = useState(null);
  const [mappingSnapshot, setMappingSnapshot] = useState(null);
  const [resumeError, setResumeError] = useState(null);
  // HEADCOUNT-IMPORT: headcount sessions require date confirmation between
  // Map and Validate. showDateConfirm tracks whether the sub-step card is
  // visible; headcountDateConfirmed holds the date once saved to the server.
  const [showDateConfirm, setShowDateConfirm]           = useState(false);
  const [headcountDateConfirmed, setHeadcountDateConfirmed] = useState(null);

  const hasLegacyWarnings    = (stageResult?.profileWarnings?.length ?? 0) > 0;
  const isAdmin              = currentRole === "admin";
  const isHeadcountSession   = stageResult?.detectedDataType === "headcount";

  // INGEST-HISTORY-SIMPLIFY-S1: resume a staged/in-review session from the
  // Ingestion history panel, restoring the wizard to the furthest step the
  // session had already reached rather than always forcing Validate.
  // - "staged": diffs haven't been generated/reviewed yet, but column
  //   mapping is already resolved — resume at Map (step 2) so the mapping
  //   can be confirmed before diffs are computed.
  // - "in_review" / "partially_applied": diffs already exist and are
  //   mid-review — resume straight to Validate (step 3).
  // - PDF sessions never have a Map step, so they always resume at step 3.
  useEffect(() => {
    if (!resumeSessionId) return;
    let cancelled = false;
    setResumeError(null);
    getImportSession(resumeSessionId)
      .then(({ session }) => {
        if (cancelled || !session) return;
        const fromPdf = session.sourceType === "pdf_abstraction";
        setStageResult({
          sessionId:           session.id,
          detectedSourceType:  session.sourceType,
          columnMappings:      session.columnMappings ?? [],
          profileWarnings:     [],
        });
        setIsPdfSession(fromPdf);
        setValidationStatus(null);
        setLegacyOverride(false);
        setNeedsEyesCount(0);
        const furthestStep = fromPdf
          ? 3
          : session.status === "staged"
            ? 2
            : 3;
        setStep(furthestStep);
      })
      .catch((err) => {
        if (cancelled) return;
        setResumeError(err instanceof Error ? err : new Error(String(err)));
      });
    return () => { cancelled = true; };
  }, [resumeSessionId]);

  // Restore wizard state from localStorage after a page refresh.
  // Only activates when there is no explicit resumeSessionId prop (the history
  // panel resume path already handles that case above).
  useEffect(() => {
    if (resumeSessionId) return;
    let stored;
    try {
      const raw = localStorage.getItem(WIZARD_STORAGE_KEY);
      stored = raw ? JSON.parse(raw) : null;
    } catch {
      stored = null;
    }
    if (!stored?.sessionId) return;

    let cancelled = false;
    getImportSession(stored.sessionId)
      .then(({ session }) => {
        if (cancelled || !session) return;
        // Terminal sessions cannot be continued — clear the stale entry.
        const TERMINAL = new Set(["applied", "partially_applied", "rejected", "expired", "failed"]);
        if (TERMINAL.has(session.status)) {
          try { localStorage.removeItem(WIZARD_STORAGE_KEY); } catch {}
          return;
        }
        const fromPdf      = session.sourceType === "pdf_abstraction";
        const isHeadcount  = session.dataType === "headcount";
        setStageResult({
          sessionId:          session.id,
          detectedSourceType: session.sourceType,
          detectedDataType:   session.dataType,
          columnMappings:     session.columnMappings ?? [],
          profileWarnings:    [],
          sourceFilename:     session.sourceFilename ?? null,
          confirmedAsOfDate:  session.confirmedAsOfDate ?? null,
        });
        setIsPdfSession(fromPdf);
        setValidationStatus(null);
        setLegacyOverride(false);
        setNeedsEyesCount(0);

        if (isHeadcount && session.confirmedAsOfDate) {
          // Date already confirmed — skip the date-confirm card and go to Validate.
          setHeadcountDateConfirmed(session.confirmedAsOfDate);
          setShowDateConfirm(false);
          setStep(3);
        } else if (isHeadcount && !fromPdf) {
          // Headcount session, date not yet confirmed — resume at the date-confirm card.
          setShowDateConfirm(true);
          setStep(2);
        } else {
          // Non-headcount: pick the furthest-reached step.
          const furthestStep = fromPdf ? 3 : session.status === "staged" ? 2 : 3;
          setStep(furthestStep);
        }
      })
      .catch(() => {
        // Session gone or user unauthorized — clear the stale localStorage entry.
        try { localStorage.removeItem(WIZARD_STORAGE_KEY); } catch {}
      });
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUploadComplete = useCallback((result, _file) => {
    setStageResult(result);
    setValidationStatus(null);
    setLegacyOverride(false);
    const fromPdf = result.detectedSourceType === "pdf_abstraction";
    setIsPdfSession(fromPdf);
    // Seed the initial needs-eyes count for the status strip.
    const initialNeedsEyes = (result.columnMappings ?? []).filter(
      (c) => c.section === "needs-eyes",
    ).length;
    setNeedsEyesCount(initialNeedsEyes);
    // Persist the session so a page refresh can resume from where we left off.
    try { localStorage.setItem(WIZARD_STORAGE_KEY, JSON.stringify({ sessionId: result.sessionId })); } catch {}
    // PDF goes directly to Validate (step 3); tabular goes to Map (step 2).
    setStep(fromPdf ? 3 : 2);
  }, []);

  const handleReset = useCallback(() => {
    setStep(1);
    setStageResult(null);
    setValidationStatus(null);
    setIsPdfSession(false);
    setLegacyOverride(false);
    setNeedsEyesCount(0);
    setApplyResult(null);
    setMappingSnapshot(null);
    setBusy(false);
    setShowDateConfirm(false);
    setHeadcountDateConfirmed(null);
    try { localStorage.removeItem(WIZARD_STORAGE_KEY); } catch {}
  }, []);

  const handleClearExisting = useCallback(() => {
    setStageResult(null);
    setValidationStatus(null);
    try { localStorage.removeItem(WIZARD_STORAGE_KEY); } catch {}
  }, []);

  // INGEST-SUMMARY-SCREEN-S1: ImportReviewPanel calls this on a clean apply
  // instead of showing its own inline success screen; advance to Step 5.
  const handleApplied = useCallback((result) => {
    setApplyResult(result);
    setStep(5);
  }, []);

  const handleViewDataset = useCallback(() => {
    onViewDataset?.(applyResult?.dataType ?? "leases");
  }, [onViewDataset, applyResult]);

  if (currentRole === "viewer") {
    return <ViewerNotice />;
  }

  return (
    <div style={S.wizard} data-testid="import-wizard">
      <Stepper step={step} />
      <WizardStatusStrip
        step={step}
        stageResult={stageResult}
        validationStatus={validationStatus}
        needsEyesCount={needsEyesCount}
      />

      {resumeError && (
        <div
          data-testid="resume-session-error"
          role="alert"
          style={{
            background: T.alertErrBg,
            border: `1px solid ${T.red}`,
            borderRadius: 6,
            padding: "8px 12px",
            marginBottom: 12,
            fontSize: T.fsSm,
            color: T.dark,
          }}
        >
          Couldn't resume session {resumeSessionId}: {resumeError.message}
        </div>
      )}

      <div style={S.body}>
        {step === 1 && (
          <UploadStep
            onComplete={handleUploadComplete}
            busy={busy}
            setBusy={setBusy}
            existingResult={stageResult}
            onClearExisting={handleClearExisting}
          />
        )}
        {step === 2 && stageResult && !showDateConfirm && (
          <MapStep
            stageResult={stageResult}
            onComplete={isHeadcountSession ? () => setShowDateConfirm(true) : () => setStep(3)}
            onBack={() => setStep(1)}
            isAdmin={isAdmin}
            legacyOverride={legacyOverride}
            onLegacyOverrideChange={setLegacyOverride}
            onNeedsEyesChange={setNeedsEyesCount}
            onMappingSnapshotChange={setMappingSnapshot}
          />
        )}
        {step === 2 && stageResult && showDateConfirm && (
          <AsOfDateCard
            sessionId={stageResult.sessionId}
            sourceFilename={stageResult.sourceFilename ?? null}
            persistedDate={stageResult.confirmedAsOfDate ?? null}
            onConfirmed={(date) => setHeadcountDateConfirmed(date)}
          />
        )}
        {step === 3 && stageResult && (
          <ValidateStep
            sessionId={stageResult.sessionId}
            onBack={() => setStep(isPdfSession ? 1 : 2)}
            onDone={handleReset}
            onValidate={setValidationStatus}
          />
        )}
        {step === 4 && (
          <div data-testid="review-step" style={{ display: "contents" }}>
            <ImportReviewPanel
              sessionId={stageResult?.sessionId ?? null}
              onBack={handleReset}
              onApplied={handleApplied}
            />
          </div>
        )}
        {step === 5 && (
          <DoneStep
            applyResult={applyResult}
            mappingSnapshot={mappingSnapshot}
            onOpenImportsQueue={onOpenImportsQueue}
            onViewDataset={onViewDataset ? handleViewDataset : undefined}
            onDone={handleReset}
          />
        )}
      </div>

      {/* Footer navigation */}
      <div style={S.footer}>
        {step > 1 && step < 4 ? (
          <button
            type="button"
            style={{ ...S.btnBase, ...S.btnSecondary }}
            onClick={() => {
              if (step === 2 && showDateConfirm) {
                setShowDateConfirm(false);
                setHeadcountDateConfirmed(null);
              } else {
                setStep((s) => s - 1);
              }
            }}
            disabled={busy}
            data-testid="wizard-back-btn"
          >
            ← Back
          </button>
        ) : (
          <div />
        )}

        <div style={{ display: "flex", gap: 8 }}>
          {step === 2 && !showDateConfirm && (
            <button
              type="button"
              style={{
                ...S.btnBase,
                ...S.btnPrimary,
                opacity: (busy || (hasLegacyWarnings && !legacyOverride)) ? 0.5 : 1,
                cursor: (hasLegacyWarnings && !legacyOverride) ? "not-allowed" : "pointer",
              }}
              onClick={() => {
                if (isHeadcountSession) {
                  setShowDateConfirm(true);
                } else {
                  setStep(3);
                }
              }}
              disabled={busy || (hasLegacyWarnings && !legacyOverride)}
              title={
                hasLegacyWarnings && !legacyOverride
                  ? "Re-export or rename legacy columns before continuing"
                  : undefined
              }
              data-testid="wizard-next-btn"
            >
              Next →
            </button>
          )}
          {step === 2 && showDateConfirm && (
            <button
              type="button"
              style={{
                ...S.btnBase,
                ...S.btnPrimary,
                opacity: (busy || !headcountDateConfirmed) ? 0.5 : 1,
                cursor: !headcountDateConfirmed ? "not-allowed" : "pointer",
              }}
              onClick={() => setStep(3)}
              disabled={busy || !headcountDateConfirmed}
              title={!headcountDateConfirmed ? "Confirm the as-of date above before continuing" : undefined}
              data-testid="wizard-validate-btn"
            >
              Validate →
            </button>
          )}
          {step === 3 && (
            <>
              <button
                type="button"
                style={{
                  ...S.btnBase,
                  ...S.btnPrimary,
                  opacity: validationStatus === "errors" || !validationStatus ? 0.5 : 1,
                }}
                onClick={() => setStep(4)}
                disabled={validationStatus === "errors" || !validationStatus}
                data-testid="validate-continue-btn"
              >
                Review diffs →
              </button>
              <button
                type="button"
                style={{ ...S.btnBase, ...S.btnSecondary }}
                onClick={handleReset}
                data-testid="wizard-done-btn"
              >
                Import another file
              </button>
            </>
          )}
          {step === 4 && (
            <>
              {onOpenImportsQueue && (
                <button
                  type="button"
                  style={{ ...S.btnBase, ...S.btnLinkPlain }}
                  onClick={() => onOpenImportsQueue(stageResult?.sessionId ?? null)}
                  data-testid="review-staged-imports-btn"
                >
                  Save for later - review in Pending imports
                </button>
              )}
              <button
                type="button"
                style={{ ...S.btnBase, ...S.btnSecondary }}
                onClick={handleReset}
                data-testid="wizard-done-btn"
              >
                Import another file
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
